// Step 3 — Content Builder (EMAIL-007 / EMAIL-008 / EMAIL-009 / EMAIL-CREATE-012/014/015/016).
//
// Two phases per the Step 3 screenshot set:
//   1. Template GALLERY — 8 numbered template cards with previews, gold Select
//      buttons and Best For / Design Tip callouts (verbatim copy).
//   2. BUILDER — 3-pane layout (Blocks palette / email canvas / settings) with
//      real per-block settings panels driven by the canonical 20-block model in
//      emailBuilderData.ts.
//
// Quirk corrections applied from the mocks (13_DESIGN_JUDGMENT rules — each
// one logged in the module changelog):
//   - "fully customization" -> "fully customizable" (gallery helper [sic]).
//   - "Best For:Time-sensitive" -> "Best For: Time-sensitive" (missing space).
//   - "7.  FROM THE DESK" double space -> single space (mock kerning artifact).
//   - "Karen Hattle" -> "Karen Hattie" (sample-name typo; other shots use Hattie).
//   - "Template Settngs" -> "Template Settings" (mock tab typo).
//   - Change-event popup subtitle "Change to another event by Select an
//     upcoming event..." -> "Select an upcoming event to feature in your
//     newsletter template." (mock grammar artifact).
//   - Selected-block chip always shows the REAL block name (the mocks
//     copy-pasted "Block | Grid Icons and Title" onto non-grid blocks).

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CachedIcon from "@mui/icons-material/Cached";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ColorizeIcon from "@mui/icons-material/Colorize";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FormatAlignCenterOutlinedIcon from "@mui/icons-material/FormatAlignCenterOutlined";
import FormatAlignJustifyOutlinedIcon from "@mui/icons-material/FormatAlignJustifyOutlined";
import FormatAlignLeftOutlinedIcon from "@mui/icons-material/FormatAlignLeftOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import {
  CmpEventStatusEnum,
  CmpEventVisibilityEnum,
  GetCmpManageEventsQuery,
  useGetCmpManageEventsQuery,
  useGetChamberMemberProSettingsQuery,
} from "../../../graphql/types";
import { normalizeCmpTimeZone } from "../../../utils/cmpTimeZones";
import { RoutePaths } from "../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../hooks/useGetChamberFromUserIdInAuth";
import { emailImageAccept } from "./emailAssetUpload";
import {
  AdvertisingPdfSettings,
  AdvocacyUpdateBannerSettings,
  AlertBannerSettings,
  ArticleListSettings,
  EmailBannerSettings,
  EmailBuilderBlock,
  EmailBuilderBlockOf,
  EmailBuilderBlockSettingsMap,
  EmailFooterProfile,
  EmailBuilderBlockType,
  EmailBuilderEventItem,
  EmailBuilderIconKey,
  EmailBuilderState,
  UpdateEmailBuilderState,
  EmailTemplateKey,
  EmailTemplatePreset,
  EventAnnouncementSettings,
  FourGridIconAndTitleSettings,
  InquiriesSettings,
  KeyLinksSettings,
  MemberSpotlightSettings,
  SignatureSettings,
  SixGridIconAndTitleSettings,
  SpecialGuestSponsorSettings,
  SplitArticleWithBorderSettings,
  SponsorsListSettings,
  TextAndButtonHeroBannerSettings,
  TextAndEmailContentSettings,
  TextHeroBannerSettings,
  UpcomingEventsListSettings,
  UpdateBannerSettings,
  createArticleListItem,
  createEmailBuilderBlock,
  createEventItem,
  createIconGridItem,
  createKeyLinkItem,
  createMemberSpotlightItem,
  createSplitArticleItem,
  createSponsorItem,
  emailBuilderBlockLabels,
  emailBuilderColors,
  emailBuilderIconOptions,
  emailBuilderPaletteItems,
  emailTemplatePresets,
  getEmailThemeQuickPalette,
} from "./emailBuilderData";
import EmailFooterPreview from "./EmailFooterPreview";
import { EmailFormState } from "./emailMockData";
import {
  UpdateEmailField,
  inputClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  sectionClassName,
  stepDescriptionClassName,
  stepTitleClassName,
} from "./emailWizardShared";

// ---------------------------------------------------------------------------
// Shared class names + small helpers
// ---------------------------------------------------------------------------

const settingLabelClassName =
  "block text-[12px] font-extrabold uppercase tracking-wide text-gray-950";
const colorRowLabelClassName = "text-[15px] font-semibold text-gray-950";
const goldPillButtonClassName =
  "inline-flex min-h-[38px] shrink-0 items-center gap-1.5 rounded-full bg-cpaGold px-4 text-[13px] font-extrabold text-white transition hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:opacity-60";
const goldFullWidthButtonClassName =
  "flex min-h-[46px] w-full items-center justify-center gap-1 rounded-md bg-cpaGold px-4 text-[15px] font-extrabold text-white transition hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:opacity-60";
const itemTrashButtonClassName =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-red-300 bg-white text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

const maxBuilderPdfBytes = 10 * 1024 * 1024;

// Line icons used for icon tiles on the canvas and in settings panels (the
// email HTML renderer keeps its conservative glyphs — emailBuilderData).
const blockIconComponents: Record<
  EmailBuilderIconKey,
  typeof CalendarMonthOutlinedIcon
> = {
  calendar: CalendarMonthOutlinedIcon,
  megaphone: CampaignOutlinedIcon,
  memberSearch: PersonSearchOutlinedIcon,
  personAdd: PersonAddAltOutlinedIcon,
  people: GroupsOutlinedIcon,
  briefcase: WorkOutlineOutlinedIcon,
  bank: AccountBalanceOutlinedIcon,
  phone: PhoneIphoneOutlinedIcon,
  tag: LocalOfferOutlinedIcon,
  pencil: CreateOutlinedIcon,
  warning: WarningAmberOutlinedIcon,
  sparkle: AutoAwesomeOutlinedIcon,
  bolt: BoltOutlinedIcon,
  dollar: MonetizationOnOutlinedIcon,
  gavel: GavelOutlinedIcon,
  email: MailOutlineIcon,
  clock: AccessTimeOutlinedIcon,
  mapPin: PlaceOutlinedIcon,
  link: LinkOutlinedIcon,
};

const BlockIcon = ({
  icon,
  fontSize = "medium",
}: {
  icon: EmailBuilderIconKey;
  fontSize?: "small" | "medium" | "large";
}) => {
  const IconComponent = blockIconComponents[icon];
  return <IconComponent fontSize={fontSize} />;
};

// ---------------------------------------------------------------------------
// Simple whole-field rich text (B / U / I) — stored as the limited markup the
// email HTML renderer supports (<b>/<u>/<i>), never free-form HTML.
// ---------------------------------------------------------------------------

type RichTextMarks = { bold: boolean; underline: boolean; italic: boolean };

const richTextWrappers: Array<{
  key: keyof RichTextMarks;
  open: string;
  close: string;
}> = [
  { key: "bold", open: "<b>", close: "</b>" },
  { key: "underline", open: "<u>", close: "</u>" },
  { key: "italic", open: "<i>", close: "</i>" },
];

const getRichTextParts = (
  value: string
): { text: string; marks: RichTextMarks } => {
  let text = value;
  const marks: RichTextMarks = { bold: false, underline: false, italic: false };
  let unwrapped = true;
  while (unwrapped) {
    unwrapped = false;
    for (const wrapper of richTextWrappers) {
      if (
        text.startsWith(wrapper.open) &&
        text.endsWith(wrapper.close) &&
        text.length >= wrapper.open.length + wrapper.close.length
      ) {
        text = text.slice(wrapper.open.length, text.length - wrapper.close.length);
        marks[wrapper.key] = true;
        unwrapped = true;
      }
    }
  }
  return { text, marks };
};

const buildRichTextValue = (text: string, marks: RichTextMarks): string => {
  let value = text;
  if (marks.italic) value = `<i>${value}</i>`;
  if (marks.underline) value = `<u>${value}</u>`;
  if (marks.bold) value = `<b>${value}</b>`;
  return value;
};

const richTextStyle = (marks: RichTextMarks): React.CSSProperties => ({
  fontWeight: marks.bold ? 800 : undefined,
  textDecoration: marks.underline ? "underline" : undefined,
  fontStyle: marks.italic ? "italic" : undefined,
});

// Canvas-side render of a stored rich-text value.
const RichText = ({
  value,
  className,
  style,
}: {
  value: string;
  className?: string;
  style?: React.CSSProperties;
}) => {
  const { text, marks } = getRichTextParts(value);
  return (
    <span
      className={`whitespace-pre-line ${className || ""}`}
      style={{ ...richTextStyle(marks), ...style }}
    >
      {text}
    </span>
  );
};

const richTextPlain = (value: string): string => getRichTextParts(value).text;

// ---------------------------------------------------------------------------
// Color picker (BINDING "Color Selection" annotation — gradient square,
// eyedropper, hue + opacity sliders, HEX/R/G/B inputs, plus the 4-swatch
// "Color Palette" quick strip). Same picker behavior as Step 2; local copy
// because the step files are owned by different agents — hoisting both into
// emailWizardShared is an integrator cleanup.
// ---------------------------------------------------------------------------

type HsvaColor = { h: number; s: number; v: number; a: number };

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const parseHexColor = (
  value: string
): { r: number; g: number; b: number; a: number } | null => {
  let hex = value.trim().replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }
  if (hex.length !== 6 && hex.length !== 8) return null;
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
    a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1,
  };
};

const channelToHex = (channel: number) =>
  clampNumber(Math.round(channel), 0, 255)
    .toString(16)
    .padStart(2, "0")
    .toUpperCase();

const rgbaToHexColor = (r: number, g: number, b: number, a: number) =>
  `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}${
    a >= 1 ? "" : channelToHex(a * 255)
  }`;

const rgbToHsv = (r: number, g: number, b: number) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let h = 0;
  if (delta) {
    if (max === red) h = ((green - blue) / delta) % 6;
    else if (max === green) h = (blue - red) / delta + 2;
    else h = (red - green) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : delta / max, v: max };
};

const hsvToRgb = (h: number, s: number, v: number) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
};

// "Text color will be automatically selected based on background color."
const getReadableTextColor = (backgroundHex: string): string => {
  const rgb = parseHexColor(backgroundHex);
  if (!rgb) return emailBuilderColors.darkNavy;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.55 ? emailBuilderColors.darkNavy : emailBuilderColors.white;
};

type EyeDropperResult = { sRGBHex: string };
type EyeDropperConstructor = new () => { open: () => Promise<EyeDropperResult> };

const getEyeDropperConstructor = (): EyeDropperConstructor | null =>
  typeof window !== "undefined" && "EyeDropper" in window
    ? (window as unknown as { EyeDropper: EyeDropperConstructor }).EyeDropper
    : null;

const builderColorSliderCss = `
.cmp-builder-color-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 12px;
  border-radius: 9999px;
  outline: none;
}
.cmp-builder-color-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  height: 18px;
  width: 18px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #9ca3af;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
.cmp-builder-color-slider::-moz-range-thumb {
  height: 18px;
  width: 18px;
  border-radius: 9999px;
  background: #ffffff;
  border: 1px solid #9ca3af;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.35);
  cursor: pointer;
}
`;

const BuilderColorPicker = ({
  color,
  onChange,
}: {
  color: string;
  onChange: (hex: string) => void;
}) => {
  const initial = parseHexColor(color) || { r: 0, g: 0, b: 0, a: 1 };
  const [hsva, setHsva] = useState<HsvaColor>(() => ({
    ...rgbToHsv(initial.r, initial.g, initial.b),
    a: initial.a,
  }));
  const [hexDraft, setHexDraft] = useState(() =>
    rgbaToHexColor(initial.r, initial.g, initial.b, initial.a)
  );

  const rgb = hsvToRgb(hsva.h, hsva.s, hsva.v);
  const roundedRgb = {
    r: Math.round(rgb.r),
    g: Math.round(rgb.g),
    b: Math.round(rgb.b),
  };
  const currentHex = rgbaToHexColor(roundedRgb.r, roundedRgb.g, roundedRgb.b, hsva.a);
  const eyeDropperSupported = !!getEyeDropperConstructor();

  const applyHsva = (next: HsvaColor) => {
    setHsva(next);
    const nextRgb = hsvToRgb(next.h, next.s, next.v);
    const hex = rgbaToHexColor(nextRgb.r, nextRgb.g, nextRgb.b, next.a);
    setHexDraft(hex);
    onChange(hex);
  };

  const applyRgbChannel = (channel: "r" | "g" | "b", value: number) => {
    const nextRgb = {
      ...roundedRgb,
      [channel]: clampNumber(Math.round(value) || 0, 0, 255),
    };
    setHsva({ ...rgbToHsv(nextRgb.r, nextRgb.g, nextRgb.b), a: hsva.a });
    const hex = rgbaToHexColor(nextRgb.r, nextRgb.g, nextRgb.b, hsva.a);
    setHexDraft(hex);
    onChange(hex);
  };

  const applyHexDraft = (value: string) => {
    setHexDraft(value);
    const parsed = parseHexColor(value);
    if (!parsed) return;
    setHsva({ ...rgbToHsv(parsed.r, parsed.g, parsed.b), a: parsed.a });
    onChange(rgbaToHexColor(parsed.r, parsed.g, parsed.b, parsed.a));
  };

  const pickWithEyeDropper = async () => {
    const EyeDropperCtor = getEyeDropperConstructor();
    if (!EyeDropperCtor) return;
    try {
      const result = await new EyeDropperCtor().open();
      applyHexDraft(result.sRGBHex);
    } catch {
      // Color picking was canceled.
    }
  };

  const updateSaturationFromPointer = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    applyHsva({
      ...hsva,
      s: clampNumber((event.clientX - bounds.left) / bounds.width, 0, 1),
      v: clampNumber(1 - (event.clientY - bounds.top) / bounds.height, 0, 1),
    });
  };

  return (
    <div>
      <style>{builderColorSliderCss}</style>
      <div
        className="relative h-36 w-full cursor-crosshair rounded"
        role="presentation"
        style={{
          background: `linear-gradient(to top, #000000, rgba(0, 0, 0, 0)), linear-gradient(to right, #ffffff, hsl(${Math.round(
            hsva.h
          )}, 100%, 50%))`,
        }}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          updateSaturationFromPointer(event);
        }}
        onPointerMove={(event) => {
          if (event.buttons === 1) updateSaturationFromPointer(event);
        }}
      >
        <span
          className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
          style={{
            left: `${hsva.s * 100}%`,
            top: `${(1 - hsva.v) * 100}%`,
            backgroundColor: `rgb(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b})`,
          }}
        />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={pickWithEyeDropper}
          disabled={!eyeDropperSupported}
          title={
            eyeDropperSupported
              ? "Pick a color from the screen"
              : "Color picking from the screen is not supported in this browser"
          }
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-[#2F6FED] transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ColorizeIcon fontSize="small" />
        </button>
        <div className="flex-1 space-y-2.5">
          <input
            type="range"
            min={0}
            max={360}
            step={1}
            value={Math.round(hsva.h)}
            aria-label="Hue"
            onChange={(event) =>
              applyHsva({ ...hsva, h: Number(event.target.value) })
            }
            className="cmp-builder-color-slider w-full"
            style={{
              background:
                "linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)",
            }}
          />
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(hsva.a * 100)}
            aria-label="Opacity"
            onChange={(event) =>
              applyHsva({ ...hsva, a: Number(event.target.value) / 100 })
            }
            className="cmp-builder-color-slider w-full"
            style={{
              background: `linear-gradient(to right, rgba(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b}, 0), rgb(${roundedRgb.r}, ${roundedRgb.g}, ${roundedRgb.b})), repeating-conic-gradient(#e5e7eb 0% 25%, #ffffff 0% 50%) 0 0 / 12px 12px`,
            }}
          />
        </div>
      </div>
      <div className="mt-3 flex items-end gap-2">
        <label className="block flex-1">
          <span className="text-[11px] font-extrabold uppercase text-gray-500">
            HEX
          </span>
          <input
            value={hexDraft}
            onChange={(event) => applyHexDraft(event.target.value)}
            onBlur={() => setHexDraft(currentHex)}
            className="mt-1 h-9 w-full rounded border border-gray-300 px-2 text-[13px] text-gray-950 outline-none focus:border-cpaGold"
          />
        </label>
        {(["r", "g", "b"] as const).map((channel) => (
          <label key={channel} className="block w-[52px]">
            <span className="text-[11px] font-extrabold uppercase text-gray-500">
              {channel.toUpperCase()}
            </span>
            <input
              type="number"
              min={0}
              max={255}
              value={roundedRgb[channel]}
              onChange={(event) =>
                applyRgbChannel(channel, Number(event.target.value))
              }
              className="mt-1 h-9 w-full rounded border border-gray-300 px-1.5 text-center text-[13px] text-gray-950 outline-none focus:border-cpaGold"
            />
          </label>
        ))}
      </div>
    </div>
  );
};

// Label-left / swatch-right color row. The swatch opens the full picker plus
// the theme quick palette (blue / light blue / black / white for the blue
// theme, following the Step 2 Default Content Theme).
const ColorSwatchRow = ({
  label,
  color,
  quickPalette,
  disabled,
  onChange,
}: {
  label: string;
  color: string;
  quickPalette: string[];
  disabled: boolean;
  onChange: (hex: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Shared dropdown behavior (SH-005, B11): close on outside click and Escape
  // like the module's other anchored menus (FilterDropdown pattern). The X /
  // toggle / selection close paths are unchanged.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center justify-between gap-3">
        <span className={colorRowLabelClassName}>{label}</span>
        <button
          type="button"
          onClick={() => setOpen((currentOpen) => !currentOpen)}
          disabled={disabled}
          title={label}
          aria-label={label}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white transition hover:border-cpaGold disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span
            className="block h-6 w-6 rounded-[4px] border border-gray-200"
            style={{ backgroundColor: color }}
          />
        </button>
      </div>
      {open ? (
        <div className="absolute right-0 top-full z-30 mt-2 w-[268px] rounded-md border border-gray-200 bg-white p-3 shadow-xl">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[12px] font-extrabold text-gray-950">{label}</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-gray-400 transition hover:text-gray-600"
              aria-label="Close color picker"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <BuilderColorPicker color={color} onChange={onChange} />
          <p className="mt-3 text-[11px] font-extrabold uppercase text-gray-500">
            Color Palette
          </p>
          <div className="mt-1.5 flex gap-2 rounded-md border border-gray-200 bg-white p-2">
            {quickPalette.map((hex, index) => (
              <button
                key={`${hex}-${index}`}
                type="button"
                onClick={() => onChange(hex)}
                title={hex}
                className={`h-8 w-8 rounded border transition hover:scale-105 ${
                  color.toUpperCase() === hex.toUpperCase()
                    ? "border-cpaGold"
                    : "border-gray-200"
                }`}
                style={{ backgroundColor: hex }}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Settings-panel field primitives
// ---------------------------------------------------------------------------

const SettingsField = ({
  label,
  children,
}: {
  label?: string;
  children: React.ReactNode;
}) => (
  <div>
    {label ? <span className={settingLabelClassName}>{label}</span> : null}
    <div className={label ? "mt-2" : ""}>{children}</div>
  </div>
);

const SettingsTextInput = ({
  label,
  value,
  placeholder,
  disabled,
  onChange,
}: {
  label?: string;
  value: string;
  placeholder?: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) => (
  <SettingsField label={label}>
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={inputClassName}
      disabled={disabled}
      placeholder={placeholder}
    />
  </SettingsField>
);

const RichTextToolbarButton = ({
  active,
  disabled,
  title,
  onClick,
  children,
}: {
  active?: boolean;
  disabled: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    aria-pressed={active}
    onClick={onClick}
    disabled={disabled}
    className={`flex h-8 min-w-[32px] items-center justify-center rounded px-1.5 text-[14px] text-gray-800 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60 ${
      active ? "bg-gray-200" : ""
    }`}
  >
    {children}
  </button>
);

const richTextToolbarDivider = (
  <span className="mx-0.5 h-5 w-px bg-gray-300" aria-hidden />
);

type TextAlignOption = "left" | "center" | "justify";

// Rich-text field with the B | U | I mini toolbar (optional insert-link button
// and optional alignment controls, exactly where the block specs show them).
// The two DESC fields (Advocacy Update Banner / Split Article with Border)
// render "B | U" + link WITHOUT the Italic button, per their block composites.
const RichTextField = ({
  label,
  value,
  disabled,
  rows,
  withLinkButton,
  withoutItalic,
  textAlign,
  onTextAlignChange,
  onChange,
}: {
  label?: string;
  value: string;
  disabled: boolean;
  rows?: number;
  withLinkButton?: boolean;
  withoutItalic?: boolean;
  textAlign?: TextAlignOption;
  onTextAlignChange?: (align: TextAlignOption) => void;
  onChange: (value: string) => void;
}) => {
  const { text, marks } = getRichTextParts(value);
  const toggleMark = (key: keyof RichTextMarks) =>
    onChange(buildRichTextValue(text, { ...marks, [key]: !marks[key] }));

  const insertLink = () => {
    const url = window.prompt(
      "Add a web link to include in this text. It will be shown as a clickable web address."
    );
    if (!url?.trim()) return;
    onChange(
      buildRichTextValue(`${text}${text.trim() ? " " : ""}${url.trim()}`, marks)
    );
  };

  const computedRows =
    rows || Math.min(6, Math.max(2, text.split("\n").length + (text.length > 90 ? 1 : 0)));

  return (
    <SettingsField label={label}>
      <div className="rounded-md border border-gray-300 bg-white transition focus-within:border-cpaGold">
        <div className="flex items-center gap-0.5 border-b border-gray-200 px-2 py-1">
          <RichTextToolbarButton
            title="Bold"
            active={marks.bold}
            disabled={disabled}
            onClick={() => toggleMark("bold")}
          >
            <span className="font-extrabold">B</span>
          </RichTextToolbarButton>
          {richTextToolbarDivider}
          <RichTextToolbarButton
            title="Underline"
            active={marks.underline}
            disabled={disabled}
            onClick={() => toggleMark("underline")}
          >
            <span className="font-extrabold underline">U</span>
          </RichTextToolbarButton>
          {withoutItalic ? null : (
            <>
              {richTextToolbarDivider}
              <RichTextToolbarButton
                title="Italic"
                active={marks.italic}
                disabled={disabled}
                onClick={() => toggleMark("italic")}
              >
                <span className="font-extrabold italic">I</span>
              </RichTextToolbarButton>
            </>
          )}
          {withLinkButton ? (
            <>
              <span className="mx-1 h-6 w-px bg-gray-300" aria-hidden />
              <RichTextToolbarButton
                title="Insert link"
                disabled={disabled}
                onClick={insertLink}
              >
                <InsertLinkIcon fontSize="small" />
              </RichTextToolbarButton>
            </>
          ) : null}
          {textAlign && onTextAlignChange ? (
            <>
              {richTextToolbarDivider}
              <RichTextToolbarButton
                title="Align left"
                active={textAlign === "left"}
                disabled={disabled}
                onClick={() => onTextAlignChange("left")}
              >
                <FormatAlignLeftOutlinedIcon fontSize="small" />
              </RichTextToolbarButton>
              {richTextToolbarDivider}
              <RichTextToolbarButton
                title="Align center"
                active={textAlign === "center"}
                disabled={disabled}
                onClick={() => onTextAlignChange("center")}
              >
                <FormatAlignCenterOutlinedIcon fontSize="small" />
              </RichTextToolbarButton>
              {richTextToolbarDivider}
              <RichTextToolbarButton
                title="Justify"
                active={textAlign === "justify"}
                disabled={disabled}
                onClick={() => onTextAlignChange("justify")}
              >
                <FormatAlignJustifyOutlinedIcon fontSize="small" />
              </RichTextToolbarButton>
            </>
          ) : null}
        </div>
        <textarea
          value={text}
          onChange={(event) => onChange(buildRichTextValue(event.target.value, marks))}
          rows={computedRows}
          disabled={disabled}
          style={richTextStyle(marks)}
          className="w-full resize-y border-0 bg-transparent px-3 py-2.5 text-[15px] text-gray-950 outline-none placeholder:text-gray-400 disabled:text-gray-500"
        />
      </div>
    </SettingsField>
  );
};

// Image upload/preview/replace/delete via the presigned-upload flow
// (EMAIL-CREATE-012 — uploadImage prop threads from EmailForm).
const ImageUploadControl = ({
  label,
  imageUrl,
  buttonLabel = "Change Image",
  disabled,
  uploadImage,
  onChange,
}: {
  label?: string;
  imageUrl: string;
  buttonLabel?: string;
  disabled: boolean;
  uploadImage: (file: File) => Promise<string>;
  onChange: (url: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setError("");
    setIsUploading(true);
    try {
      onChange(await uploadImage(file));
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "The image could not be uploaded. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <SettingsField label={label}>
      <div className="flex items-center gap-3">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="h-14 w-14 shrink-0 rounded-md border border-gray-200 bg-white object-cover"
          />
        ) : (
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 text-gray-400">
            <FileUploadOutlinedIcon fontSize="small" />
          </span>
        )}
        <button
          type="button"
          className={goldPillButtonClassName}
          onClick={() => inputRef.current?.click()}
          disabled={disabled || isUploading}
        >
          {isUploading ? (
            <CircularProgress color="inherit" size={14} />
          ) : (
            <CachedIcon fontSize="small" />
          )}
          {buttonLabel}
        </button>
        {imageUrl ? (
          <button
            type="button"
            className={itemTrashButtonClassName}
            title="Delete image"
            aria-label="Delete image"
            onClick={() => onChange("")}
            disabled={disabled || isUploading}
          >
            <DeleteOutlineIcon fontSize="small" />
          </button>
        ) : null}
      </div>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
      <input
        ref={inputRef}
        type="file"
        accept={emailImageAccept}
        className="hidden"
        disabled={disabled || isUploading}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          void handleFile(file);
        }}
      />
    </SettingsField>
  );
};

// Icon tile + gold "Change Icon" button opening a simple icon picker.
const IconPickerControl = ({
  label,
  icon,
  disabled,
  tileClassName = "border-2 border-[#3B82F6] bg-white text-[#3B82F6]",
  onChange,
}: {
  label?: string;
  icon: EmailBuilderIconKey;
  disabled: boolean;
  tileClassName?: string;
  onChange: (icon: EmailBuilderIconKey) => void;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Shared dropdown behavior (SH-005, B11): close on outside click and Escape
  // like the module's other anchored menus (FilterDropdown pattern).
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <SettingsField label={label}>
      <div className="relative flex items-center gap-3" ref={containerRef}>
        <span
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${tileClassName}`}
        >
          <BlockIcon icon={icon} />
        </span>
        <button
          type="button"
          className={goldPillButtonClassName}
          onClick={() => setOpen((currentOpen) => !currentOpen)}
          disabled={disabled}
        >
          <CachedIcon fontSize="small" />
          Change Icon
        </button>
        {open ? (
          <div className="absolute left-0 top-full z-30 mt-2 grid w-[248px] grid-cols-5 gap-1 rounded-md border border-gray-200 bg-white p-2 shadow-xl">
            {emailBuilderIconOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                title={option.label}
                aria-label={option.label}
                onClick={() => {
                  onChange(option.key);
                  setOpen(false);
                }}
                className={`flex h-10 w-10 items-center justify-center rounded-md border transition hover:border-cpaGold ${
                  option.key === icon
                    ? "border-cpaGold text-cpaGold"
                    : "border-gray-200 text-gray-700"
                }`}
              >
                <BlockIcon icon={option.key} fontSize="small" />
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </SettingsField>
  );
};

// Numbered repeat-item card with the red trash delete (confirmed through the
// shared "Delete Item?" modal).
const GridItemCard = ({
  label,
  disabled,
  headerExtra,
  onDelete,
  children,
}: {
  label: string;
  disabled: boolean;
  headerExtra?: React.ReactNode;
  onDelete?: () => void;
  children: React.ReactNode;
}) => (
  <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-[12px] font-extrabold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <div className="flex items-center gap-2">
        {headerExtra}
        {onDelete ? (
          <button
            type="button"
            className={itemTrashButtonClassName}
            title={`Delete ${label.toLowerCase()}`}
            aria-label={`Delete ${label.toLowerCase()}`}
            onClick={onDelete}
            disabled={disabled}
          >
            <DeleteOutlineIcon fontSize="small" />
          </button>
        ) : null}
      </div>
    </div>
    {children}
  </div>
);

const GridItemsHeader = ({
  title = "Grid Items",
  collapsible,
  collapsed,
  onToggle,
}: {
  title?: string;
  collapsible?: boolean;
  collapsed?: boolean;
  onToggle?: () => void;
}) =>
  collapsible ? (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between text-left"
    >
      <span className="text-[17px] font-extrabold text-gray-950">{title}</span>
      {collapsed ? (
        <KeyboardArrowDownIcon className="text-gray-500" />
      ) : (
        <KeyboardArrowUpIcon className="text-gray-500" />
      )}
    </button>
  ) : (
    <p className="text-[17px] font-extrabold text-gray-950">{title}</p>
  );

// BINDING "Delete Block" annotation: red circular trash badge, "Delete Item?"
// heading, red DELETE + dark CANCEL, red circular X close. Used for block
// deletes and repeat-item deletes — no silent deletes.
const DeleteConfirmModal = ({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-sm rounded-xl bg-white p-8 text-center shadow-2xl">
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white shadow transition hover:bg-red-600"
        >
          <CloseIcon fontSize="small" />
        </button>
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white">
          <DeleteOutlineIcon sx={{ fontSize: 34 }} />
        </span>
        <h3 className="mt-4 text-[22px] font-extrabold text-gray-950">
          Delete Item?
        </h3>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-md bg-red-500 px-6 text-[14px] font-extrabold text-white transition hover:bg-red-600"
          >
            <DeleteOutlineIcon fontSize="small" />
            DELETE
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-gray-900 px-6 text-[14px] font-extrabold text-white transition hover:bg-black"
          >
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Real-event picker (EMAIL-009 — BINDING annotations on the Upcoming Events
// composite). Lists Live + public Events-module events.
// ---------------------------------------------------------------------------

// All event date/time labels render in the CHAMBER's time zone (the same
// convention the Events module and public website use), never the browser's.
const formatEventChip = (
  utcMilli: number,
  timeZone?: string
): { month: string; day: string } => {
  const date = new Date(utcMilli);
  return {
    month: date
      .toLocaleString("en-US", { month: "short", timeZone })
      .toUpperCase(),
    day: date.toLocaleString("en-US", { day: "numeric", timeZone }),
  };
};

const formatEventTime = (utcMilli: number, timeZone?: string): string =>
  new Date(utcMilli).toLocaleString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });

const formatEventTimeRange = (
  startUtcMilli: number,
  endUtcMilli: number,
  timeZone?: string
): string =>
  `${formatEventTime(startUtcMilli, timeZone)} – ${formatEventTime(endUtcMilli, timeZone)}`;

const formatEventDateShort = (utcMilli: number, timeZone?: string): string =>
  new Date(utcMilli).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  });

const formatEventDateLong = (utcMilli: number, timeZone?: string): string =>
  new Date(utcMilli).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone,
  });

// Q-017: "Learn More ›" / "Register Now" must open the event registration page
// on the chamber WEBSITE (binding annotation). Uses the chamber website base
// URL when configured; otherwise a relative /events/<slug>/purchase path.
const buildEventRegistrationUrl = (
  chamberWebsiteUrl: string | null | undefined,
  slug: string
): string => {
  const base = (chamberWebsiteUrl || "").trim().replace(/\/+$/, "");
  return `${base}/events/${slug}/purchase`;
};

// Best-effort month/day chip parse for manually edited event date text.
const getCanvasDateParts = (date: string): { month: string; day: string } => {
  const parsed = new Date(date.trim());
  if (!Number.isNaN(parsed.getTime())) {
    return {
      month: parsed.toLocaleString("en-US", { month: "short" }).toUpperCase(),
      day: String(parsed.getDate()),
    };
  }
  const tokens = date
    .trim()
    .split(/[\s,]+/)
    .filter(Boolean);
  const startsWithWeekday = tokens[0]?.length === 3 || (tokens[0] || "").length > 5;
  const monthToken = startsWithWeekday && tokens.length > 2 ? tokens[1] : tokens[0];
  const dayToken = startsWithWeekday && tokens.length > 2 ? tokens[2] : tokens[1];
  return {
    month: (monthToken || "").slice(0, 3).toUpperCase(),
    day: (dayToken || "").replace(/\D/g, ""),
  };
};

// Row shape returned by the shared getCmpManageEvents query document.
type EventPickerRow = GetCmpManageEventsQuery["getCmpManageEvents"]["events"][number];

const EventPickerModal = ({
  mode,
  chamberId,
  initialSelectedEventId,
  disabled,
  onClose,
  onConfirm,
}: {
  mode: "add" | "change";
  chamberId: string;
  initialSelectedEventId?: string;
  disabled: boolean;
  onClose: () => void;
  onConfirm: (event: EventPickerRow) => void;
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [selectedEventId, setSelectedEventId] = useState(
    initialSelectedEventId || ""
  );

  const { data: settingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  const chamberTimeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings.setup.timeZone
  ).timeZone;

  const { data, loading } = useGetCmpManageEventsQuery({
    variables: {
      input: {
        chamberId,
        // Backend treats Published and Live as the same "published or beyond"
        // tier — a freshly published event is PUBLISHED, not LIVE.
        statuses: [CmpEventStatusEnum.Live, CmpEventStatusEnum.Published],
      },
    },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });

  // Live + public events only, respecting the publish date (EMAIL-009). The
  // shared manage-events query document does not select publishAtUtcMilli
  // yet, so the publish-date gate applies defensively once it is added.
  const availableEvents = useMemo(() => {
    const now = Date.now();
    return (data?.getCmpManageEvents.events || [])
      .filter((event) => event.visibility === CmpEventVisibilityEnum.Public)
      .filter((event) => {
        const publishAtUtcMilli = (
          event as { publishAtUtcMilli?: number | null }
        ).publishAtUtcMilli;
        return !publishAtUtcMilli || publishAtUtcMilli <= now;
      })
      .filter((event) => event.endAtUtcMilli >= now)
      .sort((a, b) => a.startAtUtcMilli - b.startAtUtcMilli);
  }, [data]);

  const filteredEvents = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return availableEvents;
    return availableEvents.filter(
      (event) =>
        event.title.toLowerCase().includes(query) ||
        event.locationLabel.toLowerCase().includes(query)
    );
  }, [availableEvents, search]);

  const selectedEvent = filteredEvents.find(
    (event) => event.id === selectedEventId
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[86vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <h3 className="text-[20px] font-extrabold text-gray-950">
              {mode === "add" ? "Add Another Event Card" : "Change to Another Event"}
            </h3>
            <p className="mt-1 text-[14px] leading-5 text-gray-600">
              Select an upcoming event to feature in your newsletter template.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 transition hover:text-gray-600"
          >
            <CloseIcon />
          </button>
        </div>
        <div className="px-6 pt-4">
          <div className="relative">
            <SearchOutlinedIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search upcoming events..."
              className={`${inputClassName} pl-11 italic placeholder:italic`}
            />
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-[13px] font-extrabold uppercase tracking-wide text-gray-950">
              AVAILABLE EVENTS ({filteredEvents.length})
            </p>
            <button
              type="button"
              className={goldPillButtonClassName}
              onClick={() => navigate(RoutePaths.chamberAdmin_events_create)}
            >
              <AddIcon fontSize="small" />
              Create New Event
            </button>
          </div>
        </div>
        <div className="mt-3 flex-1 space-y-3 overflow-y-auto px-6 pb-4">
          {loading && filteredEvents.length === 0 ? (
            <p className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Loading your upcoming events...
            </p>
          ) : filteredEvents.length === 0 ? (
            <p className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              No live public upcoming events were found. Use Create New Event to
              add one in the Events module.
            </p>
          ) : (
            filteredEvents.map((event) => {
              const chip = formatEventChip(event.startAtUtcMilli, chamberTimeZone);
              const isSelected = event.id === selectedEventId;
              return (
                <div
                  key={event.id}
                  className={`flex items-center gap-4 rounded-md border p-3 ${
                    isSelected ? "border-cpaGold bg-amber-50/40" : "border-gray-200"
                  }`}
                >
                  <span className="flex w-14 shrink-0 flex-col items-center rounded-md bg-[#3B82F6] py-1.5 text-white">
                    <span className="text-[11px] font-extrabold leading-4">
                      {chip.month}
                    </span>
                    <span className="text-[19px] font-extrabold leading-6">
                      {chip.day}
                    </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[15px] font-extrabold text-gray-950">
                      {event.title}
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-[13px] text-gray-600">
                      <PlaceOutlinedIcon sx={{ fontSize: 15 }} />
                      <span className="truncate">{event.locationLabel}</span>
                    </p>
                    <p className="mt-0.5 flex items-center gap-1 text-[13px] text-gray-600">
                      <AccessTimeOutlinedIcon sx={{ fontSize: 15 }} />
                      {formatEventTimeRange(event.startAtUtcMilli, event.endAtUtcMilli, chamberTimeZone)}
                    </p>
                  </div>
                  {isSelected ? (
                    <span className="flex items-center gap-1.5">
                      <CheckCircleIcon className="text-cpaGold" />
                      <button
                        type="button"
                        className={goldPillButtonClassName}
                        onClick={() => setSelectedEventId("")}
                      >
                        Selected
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="inline-flex min-h-[38px] items-center rounded-full border border-gray-300 bg-gray-100 px-4 text-[13px] font-extrabold text-gray-700 transition hover:bg-gray-200"
                      onClick={() => setSelectedEventId(event.id)}
                    >
                      Select
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <p className="flex items-center gap-2 text-[13px] text-gray-600">
            <InfoOutlinedIcon fontSize="small" />
            Selected events will be automatically styled for email.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className={secondaryButtonClassName}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className={primaryButtonClassName}
              disabled={disabled || !selectedEvent}
              onClick={() => selectedEvent && onConfirm(selectedEvent)}
            >
              Add Selected Event ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shared editor for the event cards of Upcoming Events List ("EVENT n
// DETAILS" / "+ Add Another Event Card") and Event Announcement ("EVENT
// DETAILS" / "+ Add Another Event").
const EventItemsEditor = ({
  blockType,
  events,
  disabled,
  chamberId,
  chamberWebsiteUrl,
  requestDelete,
  onChangeEvents,
}: {
  blockType: "upcomingEventsList" | "eventAnnouncement";
  events: EmailBuilderEventItem[];
  disabled: boolean;
  chamberId: string;
  chamberWebsiteUrl?: string | null;
  requestDelete: (onConfirm: () => void) => void;
  onChangeEvents: (events: EmailBuilderEventItem[]) => void;
}) => {
  const [picker, setPicker] = useState<
    { mode: "add" } | { mode: "change"; itemId: string } | null
  >(null);

  const { data: settingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
  });
  const chamberTimeZone = normalizeCmpTimeZone(
    settingsData?.getChamberMemberProSettings.setup.timeZone
  ).timeZone;

  const eventRowToItemFields = (
    row: EventPickerRow
  ): Omit<EmailBuilderEventItem, "id"> => ({
    eventId: row.id,
    eventTitle: row.title,
    date:
      blockType === "eventAnnouncement"
        ? formatEventDateLong(row.startAtUtcMilli, chamberTimeZone)
        : formatEventDateShort(row.startAtUtcMilli, chamberTimeZone),
    time:
      blockType === "eventAnnouncement"
        ? formatEventTimeRange(row.startAtUtcMilli, row.endAtUtcMilli, chamberTimeZone)
        : formatEventTime(row.startAtUtcMilli, chamberTimeZone),
    location: row.locationLabel,
    linkUrl: buildEventRegistrationUrl(chamberWebsiteUrl, row.slug),
  });

  const updateEventItem = (
    itemId: string,
    updates: Partial<EmailBuilderEventItem>
  ) =>
    onChangeEvents(
      events.map((eventItem) =>
        eventItem.id === itemId ? { ...eventItem, ...updates } : eventItem
      )
    );

  return (
    <div className="space-y-4">
      {events.map((eventItem, index) => (
        <GridItemCard
          key={eventItem.id}
          label={
            blockType === "eventAnnouncement" && events.length === 1
              ? "EVENT DETAILS"
              : `EVENT ${index + 1} DETAILS`
          }
          disabled={disabled}
          headerExtra={
            <button
              type="button"
              className={goldPillButtonClassName}
              onClick={() => setPicker({ mode: "change", itemId: eventItem.id })}
              disabled={disabled}
            >
              <CachedIcon fontSize="small" />
              Change Selected Event
            </button>
          }
          onDelete={() =>
            requestDelete(() =>
              onChangeEvents(events.filter((event) => event.id !== eventItem.id))
            )
          }
        >
          <SettingsTextInput
            label="EVENT TITLE"
            value={eventItem.eventTitle}
            disabled={disabled}
            onChange={(value) => updateEventItem(eventItem.id, { eventTitle: value })}
          />
          <div className="grid grid-cols-2 gap-3">
            <SettingsTextInput
              label="DATE"
              value={eventItem.date}
              disabled={disabled}
              onChange={(value) => updateEventItem(eventItem.id, { date: value })}
            />
            <SettingsTextInput
              label="TIME"
              value={eventItem.time}
              disabled={disabled}
              onChange={(value) => updateEventItem(eventItem.id, { time: value })}
            />
          </div>
          <SettingsField label="LOCATION">
            <textarea
              value={eventItem.location}
              onChange={(event) =>
                updateEventItem(eventItem.id, { location: event.target.value })
              }
              rows={blockType === "eventAnnouncement" ? 2 : 1}
              disabled={disabled}
              className="w-full resize-y rounded-md border border-gray-300 bg-white px-4 py-3 text-[15px] text-gray-950 outline-none transition placeholder:text-gray-400 focus:border-cpaGold disabled:bg-gray-100 disabled:text-gray-500"
            />
          </SettingsField>
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() => setPicker({ mode: "add" })}
        disabled={disabled}
      >
        {blockType === "eventAnnouncement"
          ? "+ Add Another Event"
          : "+ Add Another Event Card"}
      </button>
      {picker ? (
        <EventPickerModal
          mode={picker.mode}
          chamberId={chamberId}
          disabled={disabled}
          initialSelectedEventId={
            picker.mode === "change"
              ? events.find((event) => event.id === picker.itemId)?.eventId
              : undefined
          }
          onClose={() => setPicker(null)}
          onConfirm={(row) => {
            if (picker.mode === "add") {
              onChangeEvents([...events, createEventItem(eventRowToItemFields(row))]);
            } else {
              updateEventItem(picker.itemId, eventRowToItemFields(row));
            }
            setPicker(null);
          }}
        />
      ) : null}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-block settings panels (field names, order, labels, placeholders and
// helper copy transcribed verbatim from blocks-a.md / blocks-b.md)
// ---------------------------------------------------------------------------

type BlockPanelDeps = {
  disabled: boolean;
  quickPalette: string[];
  uploadImage: (file: File) => Promise<string>;
  requestDelete: (onConfirm: () => void) => void;
  chamberId: string;
  chamberWebsiteUrl?: string | null;
};

type BlockPanelProps<T extends EmailBuilderBlockType> = BlockPanelDeps & {
  block: EmailBuilderBlockOf<T>;
  onUpdate: (updates: Partial<EmailBuilderBlockSettingsMap[T]>) => void;
};

const addLinkPlaceholder = "Add link to redirect the button";
const linkToOptionalPlaceholder = "Add a link";

const TextAndButtonHeroBannerPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  onUpdate,
}: BlockPanelProps<"textAndButtonHeroBanner">) => {
  const s = block.settings;
  const patch = (updates: Partial<TextAndButtonHeroBannerSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <ImageUploadControl
        label="BACKGROUND IMAGE"
        imageUrl={s.backgroundImageUrl}
        disabled={disabled}
        uploadImage={uploadImage}
        onChange={(url) => patch({ backgroundImageUrl: url })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <RichTextField
        label="HELPER TEXT"
        value={s.helperText}
        disabled={disabled}
        onChange={(value) => patch({ helperText: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <SettingsTextInput
        label="BUTTON NAME"
        value={s.buttonName}
        disabled={disabled}
        onChange={(value) => patch({ buttonName: value })}
      />
      <ColorSwatchRow
        label="Text Color"
        color={s.buttonTextColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ buttonTextColor: hex })}
      />
      <SettingsTextInput
        label="ADD LINK"
        value={s.addLink}
        placeholder={addLinkPlaceholder}
        disabled={disabled}
        onChange={(value) => patch({ addLink: value })}
      />
    </div>
  );
};

const TextHeroBannerPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"textHeroBanner">) => {
  const s = block.settings;
  const patch = (updates: Partial<TextHeroBannerSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <RichTextField
        label="TEXT CONTENT"
        value={s.textContent}
        disabled={disabled}
        onChange={(value) => patch({ textContent: value })}
      />
    </div>
  );
};

const EmailBannerPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  onUpdate,
}: BlockPanelProps<"emailBanner">) => {
  const s = block.settings;
  const patch = (updates: Partial<EmailBannerSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <ImageUploadControl
        label="CEO IMAGE"
        imageUrl={s.ceoImageUrl}
        disabled={disabled}
        uploadImage={uploadImage}
        onChange={(url) => patch({ ceoImageUrl: url })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <SettingsTextInput
        label="BUTTON NAME"
        value={s.buttonName}
        disabled={disabled}
        onChange={(value) => patch({ buttonName: value })}
      />
      <ColorSwatchRow
        label="Button Text Color"
        color={s.buttonTextColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ buttonTextColor: hex })}
      />
      <SettingsTextInput
        label="ADD LINK"
        value={s.addLink}
        placeholder={addLinkPlaceholder}
        disabled={disabled}
        onChange={(value) => patch({ addLink: value })}
      />
    </div>
  );
};

const UpdateBannerPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"updateBanner">) => {
  const s = block.settings;
  const patch = (updates: Partial<UpdateBannerSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <RichTextField
        label="DESCRIPTION"
        value={s.description}
        disabled={disabled}
        onChange={(value) => patch({ description: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <SettingsTextInput
        label="BUTTON NAME"
        value={s.buttonName}
        disabled={disabled}
        onChange={(value) => patch({ buttonName: value })}
      />
      <ColorSwatchRow
        label="Button Text Color"
        color={s.buttonTextColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ buttonTextColor: hex })}
      />
      <SettingsTextInput
        label="ADD LINK"
        value={s.addLink}
        placeholder={addLinkPlaceholder}
        disabled={disabled}
        onChange={(value) => patch({ addLink: value })}
      />
    </div>
  );
};

const AlertBannerPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"alertBanner">) => {
  const s = block.settings;
  const patch = (updates: Partial<AlertBannerSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <IconPickerControl
        label="ALERT ICON"
        icon={s.alertIcon}
        disabled={disabled}
        tileClassName="bg-[#3B82F6] text-white"
        onChange={(icon) => patch({ alertIcon: icon })}
      />
      <RichTextField
        label="ALERT TITLE"
        value={s.alertTitle}
        disabled={disabled}
        onChange={(value) => patch({ alertTitle: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
    </div>
  );
};

const AdvocacyUpdateBannerPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"advocacyUpdateBanner">) => {
  const s = block.settings;
  const patch = (updates: Partial<AdvocacyUpdateBannerSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <IconPickerControl
        icon={s.icon}
        disabled={disabled}
        tileClassName="bg-gray-900 text-white"
        onChange={(icon) => patch({ icon })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <div className="space-y-4 rounded-lg border border-gray-200 bg-white p-4">
        <SettingsTextInput
          label="LABEL"
          value={s.label}
          disabled={disabled}
          onChange={(value) => patch({ label: value })}
        />
        <RichTextField
          label="DESCRIPTION"
          value={s.description}
          disabled={disabled}
          withLinkButton
          withoutItalic
          onChange={(value) => patch({ description: value })}
        />
      </div>
      <SettingsTextInput
        label="BUTTON NAME"
        value={s.buttonName}
        disabled={disabled}
        onChange={(value) => patch({ buttonName: value })}
      />
      <ColorSwatchRow
        label="Button Color"
        color={s.buttonColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ buttonColor: hex })}
      />
      <ColorSwatchRow
        label="Button Text Color"
        color={s.buttonTextColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ buttonTextColor: hex })}
      />
      <SettingsTextInput
        label="ADD LINK"
        value={s.addLink}
        placeholder={addLinkPlaceholder}
        disabled={disabled}
        onChange={(value) => patch({ addLink: value })}
      />
    </div>
  );
};

const MemberSpotlightPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"memberSpotlight">) => {
  const s = block.settings;
  const patch = (updates: Partial<MemberSpotlightSettings>) => onUpdate(updates);
  const updateItem = (
    itemId: string,
    updates: Partial<MemberSpotlightSettings["items"][number]>
  ) =>
    patch({
      items: s.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    });
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <GridItemsHeader />
      {s.items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled}
          onDelete={() =>
            requestDelete(() =>
              patch({ items: s.items.filter((current) => current.id !== item.id) })
            )
          }
        >
          <ImageUploadControl
            imageUrl={item.imageUrl}
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(url) => updateItem(item.id, { imageUrl: url })}
          />
          <RichTextField
            label="TITLE"
            value={item.title}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { title: value })}
          />
          <RichTextField
            label="DESCRIPTION"
            value={item.description}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { description: value })}
          />
          <SettingsTextInput
            label="BUTTON NAME"
            value={item.buttonName}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { buttonName: value })}
          />
          <SettingsTextInput
            label="ADD LINK"
            value={item.addLink}
            placeholder={addLinkPlaceholder}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { addLink: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() => patch({ items: [...s.items, createMemberSpotlightItem()] })}
        disabled={disabled}
      >
        + Add Grid Item
      </button>
    </div>
  );
};

const ArticleListPanel = ({
  block,
  disabled,
  quickPalette,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"articleList">) => {
  const s = block.settings;
  const patch = (updates: Partial<ArticleListSettings>) => onUpdate(updates);
  const updateItem = (
    itemId: string,
    updates: Partial<ArticleListSettings["items"][number]>
  ) =>
    patch({
      items: s.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    });
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <GridItemsHeader />
      {s.items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled}
          onDelete={() =>
            requestDelete(() =>
              patch({ items: s.items.filter((current) => current.id !== item.id) })
            )
          }
        >
          <RichTextField
            label="TITLE"
            value={item.title}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { title: value })}
          />
          <RichTextField
            label="DESCRIPTION"
            value={item.description}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { description: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() => patch({ items: [...s.items, createArticleListItem()] })}
        disabled={disabled}
      >
        + Add Grid Item
      </button>
    </div>
  );
};

const SplitArticleWithBorderPanel = ({
  block,
  disabled,
  quickPalette,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"splitArticleWithBorder">) => {
  const s = block.settings;
  const patch = (updates: Partial<SplitArticleWithBorderSettings>) =>
    onUpdate(updates);
  const updateItem = (
    itemId: string,
    updates: Partial<SplitArticleWithBorderSettings["items"][number]>
  ) =>
    patch({
      items: s.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    });
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      {s.items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled}
          onDelete={() =>
            requestDelete(() =>
              patch({ items: s.items.filter((current) => current.id !== item.id) })
            )
          }
        >
          <IconPickerControl
            icon={item.icon}
            disabled={disabled}
            onChange={(icon) => updateItem(item.id, { icon })}
          />
          <RichTextField
            label="TITLE"
            value={item.title}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { title: value })}
          />
          <RichTextField
            label="DESC"
            value={item.desc}
            disabled={disabled}
            withLinkButton
            withoutItalic
            onChange={(value) => updateItem(item.id, { desc: value })}
          />
          <SettingsTextInput
            label="BUTTON NAME"
            value={item.buttonName}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { buttonName: value })}
          />
          <SettingsTextInput
            label="ADD LINK"
            value={item.addLink}
            placeholder={addLinkPlaceholder}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { addLink: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() => patch({ items: [...s.items, createSplitArticleItem()] })}
        disabled={disabled}
      >
        + Add Grid Item
      </button>
    </div>
  );
};

const TextAndEmailContentPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"textAndEmailContent">) => {
  const s = block.settings;
  const patch = (updates: Partial<TextAndEmailContentSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <RichTextField
        label="TEXT CONTENT"
        value={s.textContent}
        disabled={disabled}
        rows={5}
        textAlign={s.textAlign}
        onTextAlignChange={(align) => patch({ textAlign: align })}
        onChange={(value) => patch({ textContent: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
    </div>
  );
};

const IconGridItemsEditor = ({
  items,
  disabled,
  requestDelete,
  onChangeItems,
}: {
  items: FourGridIconAndTitleSettings["items"];
  disabled: boolean;
  requestDelete: (onConfirm: () => void) => void;
  onChangeItems: (items: FourGridIconAndTitleSettings["items"]) => void;
}) => {
  const updateItem = (
    itemId: string,
    updates: Partial<FourGridIconAndTitleSettings["items"][number]>
  ) =>
    onChangeItems(
      items.map((item) => (item.id === itemId ? { ...item, ...updates } : item))
    );
  return (
    <>
      {items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled}
          onDelete={() =>
            requestDelete(() =>
              onChangeItems(items.filter((current) => current.id !== item.id))
            )
          }
        >
          <IconPickerControl
            icon={item.icon}
            disabled={disabled}
            onChange={(icon) => updateItem(item.id, { icon })}
          />
          <SettingsTextInput
            label="TITLE"
            value={item.title}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { title: value })}
          />
          <SettingsTextInput
            label="LINK TO (OPTIONAL)"
            value={item.linkTo}
            placeholder={linkToOptionalPlaceholder}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { linkTo: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() => onChangeItems([...items, createIconGridItem()])}
        disabled={disabled}
      >
        + Add Grid Item
      </button>
    </>
  );
};

const FourGridIconAndTitlePanel = ({
  block,
  disabled,
  quickPalette,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"fourGridIconAndTitle">) => {
  const s = block.settings;
  const [itemsCollapsed, setItemsCollapsed] = useState(false);
  const patch = (updates: Partial<FourGridIconAndTitleSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <GridItemsHeader
        collapsible
        collapsed={itemsCollapsed}
        onToggle={() => setItemsCollapsed((current) => !current)}
      />
      {!itemsCollapsed ? (
        <IconGridItemsEditor
          items={s.items}
          disabled={disabled}
          requestDelete={requestDelete}
          onChangeItems={(items) => patch({ items })}
        />
      ) : null}
    </div>
  );
};

const SixGridIconAndTitlePanel = ({
  block,
  disabled,
  quickPalette,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"sixGridIconAndTitle">) => {
  const s = block.settings;
  const patch = (updates: Partial<SixGridIconAndTitleSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <GridItemsHeader />
      <IconGridItemsEditor
        items={s.items}
        disabled={disabled}
        requestDelete={requestDelete}
        onChangeItems={(items) => patch({ items })}
      />
    </div>
  );
};

const UpcomingEventsListPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  requestDelete,
  chamberId,
  chamberWebsiteUrl,
  onUpdate,
}: BlockPanelProps<"upcomingEventsList">) => {
  const s = block.settings;
  const patch = (updates: Partial<UpcomingEventsListSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ImageUploadControl
        imageUrl={s.imageUrl}
        disabled={disabled}
        uploadImage={uploadImage}
        onChange={(url) => patch({ imageUrl: url })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <EventItemsEditor
        blockType="upcomingEventsList"
        events={s.events}
        disabled={disabled}
        chamberId={chamberId}
        chamberWebsiteUrl={chamberWebsiteUrl}
        requestDelete={requestDelete}
        onChangeEvents={(events) => patch({ events })}
      />
    </div>
  );
};

const EventAnnouncementPanel = ({
  block,
  disabled,
  quickPalette,
  requestDelete,
  chamberId,
  chamberWebsiteUrl,
  onUpdate,
}: BlockPanelProps<"eventAnnouncement">) => {
  const s = block.settings;
  const patch = (updates: Partial<EventAnnouncementSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <EventItemsEditor
        blockType="eventAnnouncement"
        events={s.events}
        disabled={disabled}
        chamberId={chamberId}
        chamberWebsiteUrl={chamberWebsiteUrl}
        requestDelete={requestDelete}
        onChangeEvents={(events) => patch({ events })}
      />
    </div>
  );
};

const SpecialGuestSponsorPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  onUpdate,
}: BlockPanelProps<"specialGuestSponsor">) => {
  const s = block.settings;
  const patch = (updates: Partial<SpecialGuestSponsorSettings>) =>
    onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ImageUploadControl
        label="SPONSOR LOGO"
        imageUrl={s.sponsorLogoUrl}
        disabled={disabled}
        uploadImage={uploadImage}
        onChange={(url) => patch({ sponsorLogoUrl: url })}
      />
      <RichTextField
        label="TEXT CONTENT"
        value={s.textContent}
        disabled={disabled}
        onChange={(value) => patch({ textContent: value })}
      />
    </div>
  );
};

const SponsorsListPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"sponsorsList">) => {
  const s = block.settings;
  const patch = (updates: Partial<SponsorsListSettings>) => onUpdate(updates);
  const updateSponsor = (
    sponsorId: string,
    updates: Partial<SponsorsListSettings["sponsors"][number]>
  ) =>
    patch({
      sponsors: s.sponsors.map((sponsor) =>
        sponsor.id === sponsorId ? { ...sponsor, ...updates } : sponsor
      ),
    });
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <GridItemsHeader title="SPONSOR LOGO" />
      {s.sponsors.map((sponsor, index) => (
        <GridItemCard
          key={sponsor.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled || s.sponsors.length === 1}
          onDelete={() =>
            requestDelete(() =>
              patch({
                sponsors: s.sponsors.filter((current) => current.id !== sponsor.id),
              })
            )
          }
        >
          <ImageUploadControl
            imageUrl={sponsor.logoUrl}
            buttonLabel="Change Sponsor Logo"
            disabled={disabled}
            uploadImage={uploadImage}
            onChange={(url) => updateSponsor(sponsor.id, { logoUrl: url })}
          />
          <SettingsTextInput
            label="LINK TO (OPTIONAL)"
            value={sponsor.linkTo}
            placeholder={linkToOptionalPlaceholder}
            disabled={disabled}
            onChange={(value) => updateSponsor(sponsor.id, { linkTo: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() => patch({ sponsors: [...s.sponsors, createSponsorItem()] })}
        disabled={disabled || s.sponsors.length >= 3}
      >
        + Add Sponsor
      </button>
      {s.sponsors.length >= 3 ? (
        <p className="text-[13px] text-gray-500">
          This block holds 1 to 3 sponsors.
        </p>
      ) : null}
    </div>
  );
};

const AdvertisingPdfPanel = ({
  block,
  disabled,
  quickPalette,
  uploadImage,
  onUpdate,
}: BlockPanelProps<"advertisingPdf">) => {
  const s = block.settings;
  const patch = (updates: Partial<AdvertisingPdfSettings>) => onUpdate(updates);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState("");

  const handlePdfFile = async (file?: File | null) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      setPdfError("The attachment must be a PDF file.");
      return;
    }
    if (file.size > maxBuilderPdfBytes) {
      setPdfError("The PDF must be 10MB or smaller.");
      return;
    }
    setPdfError("");
    setIsUploadingPdf(true);
    try {
      const url = await uploadImage(file);
      // The button destination is the link OR the PDF, never both.
      patch({ attachPdfUrl: url, attachPdfName: file.name, addLink: "" });
    } catch (uploadError) {
      setPdfError(
        uploadError instanceof Error
          ? uploadError.message
          : "The PDF could not be uploaded. Please try again."
      );
    } finally {
      setIsUploadingPdf(false);
    }
  };

  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ImageUploadControl
        label="IMAGE"
        imageUrl={s.imageUrl}
        disabled={disabled}
        uploadImage={uploadImage}
        onChange={(url) => patch({ imageUrl: url })}
      />
      <SettingsTextInput
        label="BUTTON NAME"
        value={s.buttonName}
        disabled={disabled}
        onChange={(value) => patch({ buttonName: value })}
      />
      <ColorSwatchRow
        label="Button Text Color"
        color={s.buttonTextColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ buttonTextColor: hex })}
      />
      <SettingsField label="ADD LINK">
        <input
          value={s.addLink}
          onChange={(event) =>
            patch({ addLink: event.target.value, attachPdfUrl: "", attachPdfName: "" })
          }
          className={inputClassName}
          disabled={disabled || !!s.attachPdfUrl}
          placeholder={addLinkPlaceholder}
        />
        {s.attachPdfUrl ? (
          <p className="mt-1.5 text-[13px] text-gray-500">
            Remove the attached PDF to link the button to a web address instead.
          </p>
        ) : null}
      </SettingsField>
      <p className={settingLabelClassName}>OR</p>
      <SettingsField label="ATTACH PDF">
        {s.attachPdfUrl ? (
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600">
              <AttachFileIcon fontSize="small" />
            </span>
            <span className="min-w-0 flex-1 truncate text-[14px] text-gray-950">
              {s.attachPdfName || "Attached PDF"}
            </span>
            <button
              type="button"
              className={itemTrashButtonClassName}
              title="Remove attached PDF"
              aria-label="Remove attached PDF"
              onClick={() => patch({ attachPdfUrl: "", attachPdfName: "" })}
              disabled={disabled || isUploadingPdf}
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => pdfInputRef.current?.click()}
            disabled={disabled || isUploadingPdf || !!s.addLink.trim()}
            title={
              s.addLink.trim()
                ? "Clear ADD LINK to attach a PDF instead"
                : "Attach a PDF"
            }
            className="flex h-16 w-16 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:border-cpaGold disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isUploadingPdf ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              <AttachFileIcon />
            )}
          </button>
        )}
        {pdfError ? <p className="mt-2 text-sm text-red-600">{pdfError}</p> : null}
        <input
          ref={pdfInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          disabled={disabled || isUploadingPdf}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            void handlePdfFile(file);
          }}
        />
      </SettingsField>
    </div>
  );
};

const KeyLinksPanel = ({
  block,
  disabled,
  quickPalette,
  requestDelete,
  onUpdate,
}: BlockPanelProps<"keyLinks">) => {
  const s = block.settings;
  const patch = (updates: Partial<KeyLinksSettings>) => onUpdate(updates);
  const updateItem = (
    itemId: string,
    updates: Partial<KeyLinksSettings["items"][number]>
  ) =>
    patch({
      items: s.items.map((item) =>
        item.id === itemId ? { ...item, ...updates } : item
      ),
    });
  return (
    <div className="space-y-5">
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Highlight Color"
        color={s.highlightColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ highlightColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <GridItemsHeader />
      {s.items.map((item, index) => (
        <GridItemCard
          key={item.id}
          label={`ITEM ${index + 1}`}
          disabled={disabled}
          onDelete={() =>
            requestDelete(() =>
              patch({ items: s.items.filter((current) => current.id !== item.id) })
            )
          }
        >
          <RichTextField
            label="BUTTON TEXT"
            value={item.buttonText}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { buttonText: value })}
          />
          <SettingsTextInput
            label="ADD LINK"
            value={item.addLink}
            placeholder={addLinkPlaceholder}
            disabled={disabled}
            onChange={(value) => updateItem(item.id, { addLink: value })}
          />
        </GridItemCard>
      ))}
      <button
        type="button"
        className={goldFullWidthButtonClassName}
        onClick={() => patch({ items: [...s.items, createKeyLinkItem()] })}
        disabled={disabled}
      >
        + Add Grid Item
      </button>
    </div>
  );
};

const InquiriesPanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"inquiries">) => {
  const s = block.settings;
  const patch = (updates: Partial<InquiriesSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="TITLE"
        value={s.title}
        disabled={disabled}
        onChange={(value) => patch({ title: value })}
      />
      <RichTextField
        label="TEXT CONTENT"
        value={s.textContent}
        disabled={disabled}
        onChange={(value) => patch({ textContent: value })}
      />
      <SettingsTextInput
        label="EMAIL"
        value={s.email}
        disabled={disabled}
        onChange={(value) => patch({ email: value })}
      />
      <SettingsTextInput
        label="PHONE NUMBER"
        value={s.phoneNumber}
        disabled={disabled}
        onChange={(value) => patch({ phoneNumber: value })}
      />
    </div>
  );
};

const SignaturePanel = ({
  block,
  disabled,
  quickPalette,
  onUpdate,
}: BlockPanelProps<"signature">) => {
  const s = block.settings;
  const patch = (updates: Partial<SignatureSettings>) => onUpdate(updates);
  return (
    <div className="space-y-5">
      <ColorSwatchRow
        label="Select Text Color"
        color={s.textColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ textColor: hex })}
      />
      <ColorSwatchRow
        label="Select Background Color"
        color={s.backgroundColor}
        quickPalette={quickPalette}
        disabled={disabled}
        onChange={(hex) => patch({ backgroundColor: hex })}
      />
      <RichTextField
        label="ADD SIGNATURE"
        value={s.addSignature}
        disabled={disabled}
        onChange={(value) => patch({ addSignature: value })}
      />
      <SettingsTextInput
        label="NAME"
        value={s.name}
        disabled={disabled}
        onChange={(value) => patch({ name: value })}
      />
      <SettingsTextInput
        label="DESIGNATION"
        value={s.designation}
        disabled={disabled}
        onChange={(value) => patch({ designation: value })}
      />
      <SettingsTextInput
        label="CHAMBER NAME"
        value={s.chamberName}
        disabled={disabled}
        onChange={(value) => patch({ chamberName: value })}
      />
    </div>
  );
};

const BlockSettingsPanel = ({
  block,
  patchBlockSettings,
  ...deps
}: BlockPanelDeps & {
  block: EmailBuilderBlock;
  patchBlockSettings: (blockId: string, updates: Record<string, unknown>) => void;
}) => {
  const onUpdateFor = (blockId: string) => (updates: Record<string, unknown>) =>
    patchBlockSettings(blockId, updates);

  switch (block.type) {
    case "textAndButtonHeroBanner":
      return (
        <TextAndButtonHeroBannerPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "textHeroBanner":
      return (
        <TextHeroBannerPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />
      );
    case "emailBanner":
      return (
        <EmailBannerPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />
      );
    case "updateBanner":
      return (
        <UpdateBannerPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />
      );
    case "alertBanner":
      return (
        <AlertBannerPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />
      );
    case "advocacyUpdateBanner":
      return (
        <AdvocacyUpdateBannerPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "memberSpotlight":
      return (
        <MemberSpotlightPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />
      );
    case "articleList":
      return (
        <ArticleListPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />
      );
    case "splitArticleWithBorder":
      return (
        <SplitArticleWithBorderPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "textAndEmailContent":
      return (
        <TextAndEmailContentPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "fourGridIconAndTitle":
      return (
        <FourGridIconAndTitlePanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "sixGridIconAndTitle":
      return (
        <SixGridIconAndTitlePanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "upcomingEventsList":
      return (
        <UpcomingEventsListPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "eventAnnouncement":
      return (
        <EventAnnouncementPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "specialGuestSponsor":
      return (
        <SpecialGuestSponsorPanel
          block={block}
          onUpdate={onUpdateFor(block.id)}
          {...deps}
        />
      );
    case "sponsorsList":
      return (
        <SponsorsListPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />
      );
    case "advertisingPdf":
      return (
        <AdvertisingPdfPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />
      );
    case "keyLinks":
      return <KeyLinksPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />;
    case "inquiries":
      return <InquiriesPanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />;
    case "signature":
      return <SignaturePanel block={block} onUpdate={onUpdateFor(block.id)} {...deps} />;
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Canvas rendering — visual approximations of each block matching the
// template screenshots (colors bound to the block settings).
// ---------------------------------------------------------------------------

const canvasLinkStyle = (color: string): React.CSSProperties => ({
  color,
  fontWeight: 800,
});

const CanvasIconTile = ({
  icon,
  color,
  background = "transparent",
  bordered = true,
  size = 52,
}: {
  icon: EmailBuilderIconKey;
  color: string;
  background?: string;
  bordered?: boolean;
  size?: number;
}) => (
  <span
    className="flex items-center justify-center rounded-xl"
    style={{
      width: size,
      height: size,
      color,
      backgroundColor: background,
      border: bordered ? `2px solid ${color}` : "none",
    }}
  >
    <BlockIcon icon={icon} />
  </span>
);

const CanvasEventCard = ({
  eventItem,
  highlightColor,
  textColor,
}: {
  eventItem: EmailBuilderEventItem;
  highlightColor: string;
  textColor: string;
}) => {
  const chip = getCanvasDateParts(eventItem.date);
  return (
    <div className="mt-4 flex items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white">
      <span
        className="flex w-16 shrink-0 flex-col items-center justify-center text-white"
        style={{ backgroundColor: highlightColor }}
      >
        <span className="text-[12px] font-extrabold">{chip.month}</span>
        <span className="text-[24px] font-extrabold leading-7">{chip.day}</span>
      </span>
      <div className="flex min-w-0 flex-1 items-start justify-between gap-3 p-4 text-left">
        <div className="min-w-0">
          <p className="text-[17px] font-extrabold" style={{ color: textColor }}>
            {eventItem.eventTitle}
          </p>
          <p className="mt-1 text-[14px] text-gray-600">
            {eventItem.date}
            {eventItem.time ? ` | ${eventItem.time}` : ""}
          </p>
          <p className="mt-0.5 whitespace-pre-line text-[14px] text-gray-600">
            {eventItem.location}
          </p>
        </div>
        {eventItem.linkUrl ? (
          <a
            href={eventItem.linkUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="shrink-0 text-[15px]"
            style={canvasLinkStyle(highlightColor)}
          >
            Learn More ›
          </a>
        ) : (
          <span className="shrink-0 text-[15px]" style={canvasLinkStyle(highlightColor)}>
            Learn More ›
          </span>
        )}
      </div>
    </div>
  );
};

const CanvasBlock = ({ block }: { block: EmailBuilderBlock }) => {
  switch (block.type) {
    case "textAndButtonHeroBanner": {
      const s = block.settings;
      return (
        <div
          className="px-9 py-14 text-left"
          style={{
            backgroundColor: s.backgroundColor,
            backgroundImage: s.backgroundImageUrl
              ? `url(${s.backgroundImageUrl})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <RichText
            value={s.title}
            className="block text-[34px] font-extrabold leading-tight"
            style={{ color: s.textColor }}
          />
          <RichText
            value={s.helperText}
            className="mt-2 block text-[19px] font-semibold"
            style={{ color: s.textColor }}
          />
          {richTextPlain(s.buttonName).trim() ? (
            <span
              className="mt-6 inline-flex min-h-[42px] items-center rounded-md px-6 text-[15px] font-extrabold"
              style={{ backgroundColor: s.highlightColor, color: s.buttonTextColor }}
            >
              {richTextPlain(s.buttonName)}
            </span>
          ) : null}
        </div>
      );
    }
    case "textHeroBanner": {
      const s = block.settings;
      return (
        <div className="px-9 py-10 text-center" style={{ backgroundColor: s.backgroundColor }}>
          <RichText
            value={s.title}
            className="block text-[28px] font-extrabold leading-tight"
            style={{ color: s.textColor }}
          />
          <RichText
            value={s.textContent}
            className="mt-2 block text-[16px] leading-6"
            style={{ color: s.textColor }}
          />
        </div>
      );
    }
    case "emailBanner": {
      const s = block.settings;
      const titleLines = richTextPlain(s.title).split("\n").filter((line) => line.trim());
      return (
        <div
          className="flex items-center gap-6 px-9 py-9 text-left"
          style={{ backgroundColor: s.backgroundColor }}
        >
          <div className="flex-1">
            {richTextPlain(s.buttonName).trim() ? (
              <span
                className="inline-block rounded px-4 py-2 text-[12px] font-extrabold uppercase tracking-[2px]"
                style={{ backgroundColor: s.highlightColor, color: s.buttonTextColor }}
              >
                {richTextPlain(s.buttonName)}
              </span>
            ) : null}
            <p className="mt-4 text-[34px] font-extrabold leading-tight">
              {titleLines.map((line, index) => {
                const isAccentLine =
                  titleLines.length > 1 && index === titleLines.length - 1;
                return (
                  <span
                    key={`${line}-${index}`}
                    className="block"
                    style={{
                      color: isAccentLine ? s.highlightColor : s.textColor,
                      fontStyle: isAccentLine ? "italic" : undefined,
                    }}
                  >
                    {line}
                  </span>
                );
              })}
            </p>
          </div>
          {s.ceoImageUrl ? (
            <img
              src={s.ceoImageUrl}
              alt=""
              className="h-44 w-40 shrink-0 rounded-md object-cover"
            />
          ) : (
            <span className="flex h-44 w-40 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 text-[13px] text-gray-400">
              CEO Image
            </span>
          )}
        </div>
      );
    }
    case "updateBanner": {
      const s = block.settings;
      return (
        <div className="px-9 py-10 text-center" style={{ backgroundColor: s.backgroundColor }}>
          <RichText
            value={s.title}
            className="block text-[28px] font-extrabold leading-tight"
            style={{ color: s.textColor }}
          />
          <RichText
            value={s.description}
            className="mx-auto mt-3 block max-w-xl text-[15px] leading-6 text-gray-600"
          />
          {richTextPlain(s.buttonName).trim() ? (
            <span
              className="mt-6 inline-flex min-h-[42px] items-center rounded-md px-7 text-[15px] font-extrabold shadow"
              style={{ backgroundColor: s.highlightColor, color: s.buttonTextColor }}
            >
              {richTextPlain(s.buttonName)}
            </span>
          ) : null}
        </div>
      );
    }
    case "alertBanner": {
      const s = block.settings;
      return (
        <div
          className="flex items-center justify-center gap-3 px-9 py-4"
          style={{ backgroundColor: s.backgroundColor, color: s.textColor }}
        >
          <BlockIcon icon={s.alertIcon} fontSize="small" />
          <RichText
            value={s.alertTitle}
            className="text-[16px] font-extrabold uppercase tracking-[3px]"
            style={{ color: s.textColor }}
          />
        </div>
      );
    }
    case "advocacyUpdateBanner": {
      const s = block.settings;
      return (
        <div className="px-7 py-6" style={{ backgroundColor: emailBuilderColors.white }}>
          <div
            className="flex items-center gap-6 rounded-xl px-8 py-7 text-left"
            style={{ backgroundColor: s.backgroundColor }}
          >
            <div className="flex-1">
              <p
                className="flex items-center gap-2 text-[13px] font-extrabold uppercase tracking-[3px]"
                style={{ color: s.textColor }}
              >
                <BlockIcon icon={s.icon} fontSize="small" />
                <RichText value={s.title} style={{ color: s.textColor }} />
              </p>
              <p
                className="mt-3 text-[26px] font-extrabold leading-tight"
                style={{ color: s.textColor }}
              >
                <RichText value={s.label} style={{ color: s.textColor }} />
              </p>
              <RichText
                value={s.description}
                className="mt-2 block text-[15px] leading-6"
                style={{ color: s.highlightColor }}
              />
            </div>
            {richTextPlain(s.buttonName).trim() ? (
              <span
                className="inline-flex min-h-[46px] max-w-[120px] items-center justify-center rounded-lg px-5 text-center text-[15px] font-extrabold"
                style={{ backgroundColor: s.buttonColor, color: s.buttonTextColor }}
              >
                {richTextPlain(s.buttonName)}
              </span>
            ) : null}
          </div>
        </div>
      );
    }
    case "memberSpotlight": {
      const s = block.settings;
      return (
        <div className="px-9 py-9" style={{ backgroundColor: s.backgroundColor }}>
          <RichText
            value={s.title}
            className="block text-center text-[24px] font-extrabold"
            style={{ color: s.textColor }}
          />
          {s.items.map((item) => (
            <div key={item.id} className="mt-6 flex items-start gap-5 text-left">
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt=""
                  className="h-28 w-28 shrink-0 rounded-md object-cover"
                />
              ) : (
                <span className="flex h-28 w-28 shrink-0 items-center justify-center rounded-md border border-dashed border-gray-300 text-[12px] text-gray-400">
                  Image
                </span>
              )}
              <div className="min-w-0 flex-1">
                <RichText
                  value={item.title}
                  className="block text-[18px] font-extrabold"
                  style={{ color: s.textColor }}
                />
                <RichText
                  value={item.description}
                  className="mt-1.5 block text-[14px] leading-6 text-gray-600"
                />
                {richTextPlain(item.buttonName).trim() ? (
                  <span
                    className="mt-2 inline-block text-[14px] underline"
                    style={canvasLinkStyle(s.highlightColor)}
                  >
                    {richTextPlain(item.buttonName)} ›
                  </span>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      );
    }
    case "articleList": {
      const s = block.settings;
      return (
        <div className="px-9 py-8 text-left" style={{ backgroundColor: s.backgroundColor }}>
          {s.items.map((item, index) => (
            <div key={item.id} className={index === 0 ? "" : "mt-6"}>
              <RichText
                value={item.title}
                className="block text-[19px] font-extrabold"
                style={{ color: s.textColor }}
              />
              <div className="mt-2 h-px w-full bg-gray-200" />
              <RichText
                value={item.description}
                className="mt-3 block max-w-xl text-[15px] leading-6 text-gray-600"
              />
            </div>
          ))}
        </div>
      );
    }
    case "splitArticleWithBorder": {
      const s = block.settings;
      return (
        <div className="px-7 py-8" style={{ backgroundColor: s.backgroundColor }}>
          <div className="flex divide-x divide-gray-200">
            {s.items.map((item) => (
              <div key={item.id} className="flex-1 px-4 text-left">
                <span style={{ color: s.highlightColor }}>
                  <BlockIcon icon={item.icon} />
                </span>
                <RichText
                  value={item.title}
                  className="mt-2 block text-[17px] font-extrabold"
                  style={{ color: s.textColor }}
                />
                <RichText
                  value={item.desc}
                  className="mt-1.5 block text-[13px] leading-5 text-gray-600"
                />
                {richTextPlain(item.buttonName).trim() ? (
                  <span
                    className="mt-2 inline-block text-[12px] font-extrabold uppercase tracking-wide"
                    style={canvasLinkStyle(s.highlightColor)}
                  >
                    {richTextPlain(item.buttonName)} ›
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "textAndEmailContent": {
      const s = block.settings;
      return (
        <div
          className="px-9 py-8"
          style={{ backgroundColor: s.backgroundColor, textAlign: s.textAlign }}
        >
          <RichText
            value={s.textContent}
            className="block text-[15px] leading-7"
            style={{ color: s.textColor }}
          />
        </div>
      );
    }
    case "fourGridIconAndTitle": {
      const s = block.settings;
      return (
        <div className="px-9 py-9 text-center" style={{ backgroundColor: s.backgroundColor }}>
          {richTextPlain(s.title).trim() ? (
            <RichText
              value={s.title}
              className="block text-[24px] font-extrabold"
              style={{ color: s.textColor }}
            />
          ) : null}
          <div className="mt-7 flex flex-wrap justify-center gap-6">
            {s.items.map((item) => (
              <div key={item.id} className="w-24">
                <div className="flex justify-center">
                  <CanvasIconTile
                    icon={item.icon}
                    color={s.highlightColor}
                    background={
                      item.highlighted ? emailBuilderColors.lightBlue : "transparent"
                    }
                  />
                </div>
                <p
                  className="mt-2 text-[12px] font-extrabold uppercase leading-4"
                  style={{ color: s.textColor }}
                >
                  {item.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "sixGridIconAndTitle": {
      const s = block.settings;
      return (
        <div className="px-9 py-9 text-center" style={{ backgroundColor: s.backgroundColor }}>
          <RichText
            value={s.title}
            className="block text-[18px] font-extrabold uppercase tracking-[2px]"
            style={{ color: s.textColor }}
          />
          <div className="mx-auto mt-7 grid max-w-lg grid-cols-3 gap-x-4 gap-y-7">
            {s.items.map((item) => (
              <div key={item.id}>
                <div className="flex justify-center">
                  <CanvasIconTile
                    icon={item.icon}
                    color={s.highlightColor}
                    bordered={false}
                    background={
                      item.highlighted ? emailBuilderColors.lightBlue : "transparent"
                    }
                  />
                </div>
                <p
                  className="mt-1.5 text-[14px] font-extrabold leading-5"
                  style={{ color: s.textColor }}
                >
                  {item.title}
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "upcomingEventsList": {
      const s = block.settings;
      return (
        <div className="px-9 py-8 text-left" style={{ backgroundColor: s.backgroundColor }}>
          <div className="flex items-center gap-3">
            <span
              className="h-7 w-1.5 rounded"
              style={{ backgroundColor: s.highlightColor }}
            />
            <RichText
              value={s.title}
              className="text-[22px] font-extrabold"
              style={{ color: s.textColor }}
            />
          </div>
          {s.imageUrl ? (
            <img
              src={s.imageUrl}
              alt=""
              className="mt-4 max-h-44 w-full rounded-md object-cover"
            />
          ) : null}
          {s.events.map((eventItem) => (
            <CanvasEventCard
              key={eventItem.id}
              eventItem={eventItem}
              highlightColor={s.highlightColor}
              textColor={s.textColor}
            />
          ))}
        </div>
      );
    }
    case "eventAnnouncement": {
      const s = block.settings;
      return (
        <div className="px-9 py-8 text-left" style={{ backgroundColor: s.backgroundColor }}>
          {richTextPlain(s.title).trim() ? (
            <RichText
              value={s.title}
              className="block text-[24px] font-extrabold"
              style={{ color: s.textColor }}
            />
          ) : null}
          {s.events.map((eventItem, eventIndex) => {
            const locationLines = eventItem.location
              .split("\n")
              .filter((line) => line.trim());
            return (
              <div key={eventItem.id} className={eventIndex === 0 ? "" : "mt-8"}>
                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-4">
                    <CanvasIconTile
                      icon="calendar"
                      color={s.highlightColor}
                      background={emailBuilderColors.lightBlue}
                      bordered={false}
                      size={46}
                    />
                    <p className="text-[16px] font-extrabold" style={{ color: s.textColor }}>
                      {eventItem.date}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <CanvasIconTile
                      icon="clock"
                      color={s.highlightColor}
                      background={emailBuilderColors.lightBlue}
                      bordered={false}
                      size={46}
                    />
                    <p className="text-[16px] font-extrabold" style={{ color: s.textColor }}>
                      {eventItem.time}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <CanvasIconTile
                      icon="mapPin"
                      color={s.highlightColor}
                      background={emailBuilderColors.lightBlue}
                      bordered={false}
                      size={46}
                    />
                    <div>
                      <p
                        className="text-[16px] font-extrabold"
                        style={{ color: s.textColor }}
                      >
                        {locationLines[0] || eventItem.location}
                      </p>
                      {locationLines.slice(1).map((line) => (
                        <p key={line} className="text-[14px] text-gray-600">
                          {line}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  {eventItem.linkUrl ? (
                    <a
                      href={eventItem.linkUrl}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(event) => event.stopPropagation()}
                      className="inline-flex min-h-[46px] items-center rounded-md px-10 text-[16px] font-extrabold text-white"
                      style={{ backgroundColor: s.highlightColor }}
                    >
                      Register Now
                    </a>
                  ) : (
                    <span
                      className="inline-flex min-h-[46px] items-center rounded-md px-10 text-[16px] font-extrabold text-white"
                      style={{ backgroundColor: s.highlightColor }}
                    >
                      Register Now
                    </span>
                  )}
                </div>
                <div className="mt-6 h-px w-full bg-gray-200" />
              </div>
            );
          })}
        </div>
      );
    }
    case "specialGuestSponsor": {
      const s = block.settings;
      return (
        <div className="px-9 py-7" style={{ backgroundColor: emailBuilderColors.white }}>
          <div
            className="rounded-lg border border-gray-200 px-8 py-7 text-center"
            style={{ backgroundColor: s.backgroundColor }}
          >
            <RichText
              value={s.title}
              className="block text-[15px] font-extrabold uppercase tracking-[3px]"
              style={{ color: s.textColor }}
            />
            <div className="mt-4 flex justify-center">
              {s.sponsorLogoUrl ? (
                <img
                  src={s.sponsorLogoUrl}
                  alt=""
                  className="max-h-20 max-w-[220px] rounded bg-white object-contain p-2"
                />
              ) : (
                <span className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed border-gray-300 text-[13px] text-gray-400">
                  Sponsor Logo
                </span>
              )}
            </div>
            <RichText
              value={s.textContent}
              className="mt-4 block text-[14px] italic"
              style={{ color: s.textColor }}
            />
          </div>
        </div>
      );
    }
    case "sponsorsList": {
      const s = block.settings;
      return (
        <div className="px-9 py-7" style={{ backgroundColor: emailBuilderColors.white }}>
          <div
            className="rounded-lg border border-gray-200 px-8 py-7 text-center"
            style={{ backgroundColor: s.backgroundColor }}
          >
            <RichText
              value={s.title}
              className="block text-[15px] font-extrabold uppercase tracking-[3px]"
              style={{ color: s.textColor }}
            />
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
              {s.sponsors.map((sponsor) =>
                sponsor.logoUrl ? (
                  <img
                    key={sponsor.id}
                    src={sponsor.logoUrl}
                    alt=""
                    className="max-h-16 max-w-[180px] rounded border border-gray-200 bg-white object-contain p-2"
                  />
                ) : (
                  <span
                    key={sponsor.id}
                    className="flex h-16 w-40 items-center justify-center rounded-md border border-dashed border-gray-300 text-[13px] text-gray-400"
                  >
                    Sponsor Logo
                  </span>
                )
              )}
            </div>
          </div>
        </div>
      );
    }
    case "advertisingPdf": {
      const s = block.settings;
      return (
        <div className="px-9 py-9 text-center" style={{ backgroundColor: s.backgroundColor }}>
          <RichText
            value={s.title}
            className="block text-[26px] font-extrabold"
            style={{ color: s.textColor }}
          />
          <div className="mt-5 flex justify-center">
            {s.imageUrl ? (
              <img
                src={s.imageUrl}
                alt=""
                className="max-h-32 max-w-[260px] rounded-md border border-gray-200 bg-white object-contain p-3"
              />
            ) : (
              <span className="flex h-24 w-56 items-center justify-center rounded-md border border-dashed border-gray-300 text-[13px] text-gray-400">
                Advertiser Image
              </span>
            )}
          </div>
          {richTextPlain(s.buttonName).trim() ? (
            <span
              className="mt-6 inline-flex min-h-[42px] items-center rounded-md px-6 text-[15px] font-extrabold"
              style={{ backgroundColor: s.highlightColor, color: s.buttonTextColor }}
            >
              {richTextPlain(s.buttonName)}
            </span>
          ) : null}
        </div>
      );
    }
    case "keyLinks": {
      const s = block.settings;
      return (
        <div className="px-9 py-7" style={{ backgroundColor: emailBuilderColors.white }}>
          <div
            className="rounded-lg border border-gray-200 px-7 py-6 text-left"
            style={{ backgroundColor: s.backgroundColor }}
          >
            <RichText
              value={s.title}
              className="block text-[19px] font-extrabold"
              style={{ color: s.textColor }}
            />
            <div className="mt-3 space-y-2.5">
              {s.items.map((item) => (
                <p
                  key={item.id}
                  className="flex items-center gap-2 text-[15px]"
                  style={canvasLinkStyle(s.highlightColor)}
                >
                  <span aria-hidden>→</span>
                  {richTextPlain(item.buttonText)}
                </p>
              ))}
            </div>
          </div>
        </div>
      );
    }
    case "inquiries": {
      const s = block.settings;
      return (
        <div className="px-9 py-7" style={{ backgroundColor: emailBuilderColors.white }}>
          <div
            className="rounded-lg border border-gray-200 px-7 py-6 text-left"
            style={{ backgroundColor: s.backgroundColor }}
          >
            <RichText
              value={s.title}
              className="block text-[15px] font-extrabold"
              style={{ color: s.textColor }}
            />
            <RichText
              value={s.textContent}
              className="mt-2 block text-[19px] font-extrabold"
              style={{ color: s.textColor }}
            />
            <p className="mt-2 text-[15px] underline" style={{ color: s.textColor }}>
              {s.email}
            </p>
            <p className="mt-1 text-[15px]" style={{ color: s.textColor }}>
              {s.phoneNumber}
            </p>
          </div>
        </div>
      );
    }
    case "signature": {
      const s = block.settings;
      return (
        <div className="px-9 py-8 text-left" style={{ backgroundColor: s.backgroundColor }}>
          <div className="h-px w-full bg-gray-900" />
          <p className="mt-5 text-[15px]" style={{ color: s.textColor }}>
            Sincerely,
          </p>
          <RichText
            value={s.addSignature}
            className="mt-3 block font-serif text-[30px] italic"
            style={{ color: s.textColor }}
          />
          <p className="mt-3 text-[16px] font-extrabold" style={{ color: s.textColor }}>
            {s.name}
          </p>
          <p className="mt-1 text-[12px] font-semibold uppercase tracking-[2px] text-gray-500">
            {[s.designation, s.chamberName].filter(Boolean).join(", ")}
          </p>
        </div>
      );
    }
    default:
      return null;
  }
};

// Selected-block chrome: blue outline + corner/midpoint handles + blue
// "Block | <name>" chip + floating dark Move/Duplicate/Delete toolbar.
const selectionHandlePositions = [
  "-left-[5px] -top-[5px]",
  "left-1/2 -top-[5px] -translate-x-1/2",
  "-right-[5px] -top-[5px]",
  "-left-[5px] top-1/2 -translate-y-1/2",
  "-right-[5px] top-1/2 -translate-y-1/2",
  "-left-[5px] -bottom-[5px]",
  "left-1/2 -bottom-[5px] -translate-x-1/2",
  "-right-[5px] -bottom-[5px]",
];

const CanvasBlockShell = ({
  block,
  selected,
  disabled,
  onSelect,
  onMove,
  onDuplicate,
  onDelete,
}: {
  block: EmailBuilderBlock;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  onMove: (direction: -1 | 1) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) => {
  const [moveMode, setMoveMode] = useState(false);

  const toolbarButtonClassName =
    "flex w-full flex-col items-center gap-0.5 px-3 py-2 text-[11px] font-bold transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onSelect();
      }}
      className={`relative cursor-pointer border-2 transition ${
        selected ? "border-[#3B82F6]" : "border-transparent hover:border-gray-200"
      }`}
    >
      {selected ? (
        <>
          <span className="absolute -top-3.5 left-3 z-20 rounded bg-[#3B82F6] px-2.5 py-1 text-[11px] font-extrabold text-white">
            Block | {emailBuilderBlockLabels[block.type]}
          </span>
          {selectionHandlePositions.map((position) => (
            <span
              key={position}
              aria-hidden
              className={`pointer-events-none absolute z-10 h-2.5 w-2.5 border border-[#3B82F6] bg-white ${position}`}
            />
          ))}
          <div className="absolute right-2 top-2 z-20 flex w-[86px] flex-col overflow-hidden rounded-md bg-gray-900 text-white shadow-lg">
            <button
              type="button"
              className={toolbarButtonClassName}
              onClick={(event) => {
                event.stopPropagation();
                setMoveMode((current) => !current);
              }}
              disabled={disabled}
            >
              <OpenWithIcon sx={{ fontSize: 18 }} />
              Move
            </button>
            {moveMode ? (
              <div className="flex border-y border-gray-700">
                <button
                  type="button"
                  className="flex flex-1 items-center justify-center py-1.5 transition hover:bg-gray-800 disabled:opacity-60"
                  title="Move up"
                  aria-label="Move block up"
                  onClick={(event) => {
                    event.stopPropagation();
                    onMove(-1);
                  }}
                  disabled={disabled}
                >
                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                </button>
                <button
                  type="button"
                  className="flex flex-1 items-center justify-center py-1.5 transition hover:bg-gray-800 disabled:opacity-60"
                  title="Move down"
                  aria-label="Move block down"
                  onClick={(event) => {
                    event.stopPropagation();
                    onMove(1);
                  }}
                  disabled={disabled}
                >
                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
            ) : null}
            <button
              type="button"
              className={toolbarButtonClassName}
              onClick={(event) => {
                event.stopPropagation();
                onDuplicate();
              }}
              disabled={disabled}
            >
              <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
              Duplicate
            </button>
            <button
              type="button"
              className={toolbarButtonClassName}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              disabled={disabled}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              Delete
            </button>
          </div>
        </>
      ) : null}
      <CanvasBlock block={block} />
    </div>
  );
};

const CanvasFooter = ({
  builderState,
  footerProfile,
}: {
  builderState: EmailBuilderState;
  footerProfile?: EmailFooterProfile | null;
}) => (
  <div className="overflow-hidden bg-slate-50">
    <EmailFooterPreview
      builderState={builderState}
      footerProfile={footerProfile}
      title="Email builder footer preview"
    />
  </div>
);

// ---------------------------------------------------------------------------
// Builder phase (3-pane) — EMAIL-CREATE-014/015/016
// ---------------------------------------------------------------------------

const emailFontOptions = [
  "Roboto",
  "Arial",
  "Georgia",
  "Helvetica",
  "Times New Roman",
  "Verdana",
];

// POL-020 (CHOOSE FONT): platform anchored single-select menu replacing the
// OS-native <select> popup. The CLOSED trigger keeps the previous select's
// exact skin (inputClassName + font-extrabold value + KeyboardArrowDownIcon
// chevron; text-left added because <button> centers its content); the OPEN
// panel uses the same chrome as FilterDropdown/ColorSwatchRow and follows the
// SH-005 dismissal pattern (outside click + Escape).
const FontSelectDropdown = ({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled: boolean;
  onChange: (font: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((currentOpen) => !currentOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`${inputClassName} appearance-none pr-12 text-left font-extrabold`}
      >
        {value}
      </button>
      <KeyboardArrowDownIcon className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
      {open ? (
        <div className="absolute z-30 mt-2 w-full rounded-md border border-gray-200 bg-white py-1 shadow-xl">
          {emailFontOptions.map((font) => (
            <button
              key={font}
              type="button"
              onClick={() => {
                onChange(font);
                setOpen(false);
              }}
              className={`block w-full px-4 py-2 text-left text-[15px] transition hover:bg-gray-50 ${
                font === value
                  ? "font-extrabold text-cpaGold"
                  : "text-gray-950"
              }`}
            >
              {font}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const CustomEmailBuilder = ({
  builderState,
  templateTitle,
  disabled,
  chamberId,
  chamberWebsiteUrl,
  footerProfile,
  uploadImage,
  onChange,
  onBackToTemplates,
}: {
  builderState: EmailBuilderState;
  templateTitle: string;
  disabled: boolean;
  chamberId: string;
  chamberWebsiteUrl?: string | null;
  footerProfile?: EmailFooterProfile | null;
  uploadImage: (file: File) => Promise<string>;
  onChange: UpdateEmailBuilderState;
  onBackToTemplates: () => void;
}) => {
  const [previewMode, setPreviewMode] = useState<"desktop" | "mobile">("desktop");
  const [settingsTab, setSettingsTab] = useState<"content" | "template">("content");
  const [pendingDelete, setPendingDelete] = useState<(() => void) | null>(null);
  const paletteRef = useRef<HTMLElement | null>(null);

  const selectedBlock =
    builderState.blocks.find((block) => block.id === builderState.selectedBlockId) ||
    null;

  const quickPalette = getEmailThemeQuickPalette(builderState.theme);
  const requestDelete = (onConfirm: () => void) => setPendingDelete(() => onConfirm);

  const updateBuilder = (
    updater: (currentState: EmailBuilderState) => EmailBuilderState
  ) => onChange(updater);

  const addBlock = (type: EmailBuilderBlockType) => {
    const block = createEmailBuilderBlock(type);
    updateBuilder((currentState) => ({
      ...currentState,
      selectedBlockId: block.id,
      blocks: [...currentState.blocks, block],
    }));
  };

  const selectBlock = (blockId: string) =>
    updateBuilder((currentState) => ({ ...currentState, selectedBlockId: blockId }));

  const patchBlockSettings = (blockId: string, updates: Record<string, unknown>) =>
    updateBuilder((currentState) => ({
      ...currentState,
      blocks: currentState.blocks.map((block) =>
        block.id === blockId
          ? ({
              ...block,
              settings: { ...block.settings, ...updates },
            } as EmailBuilderBlock)
          : block
      ),
    }));

  const duplicateBlock = (blockId: string) => {
    const sourceIndex = builderState.blocks.findIndex((block) => block.id === blockId);
    const source = builderState.blocks[sourceIndex];
    if (!source) return;
    const duplicated = JSON.parse(JSON.stringify(source)) as EmailBuilderBlock;
    duplicated.id = `${source.type}-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2, 8)}`;
    updateBuilder((currentState) => ({
      ...currentState,
      selectedBlockId: duplicated.id,
      blocks: [
        ...currentState.blocks.slice(0, sourceIndex + 1),
        duplicated,
        ...currentState.blocks.slice(sourceIndex + 1),
      ],
    }));
  };

  const deleteBlock = (blockId: string) =>
    updateBuilder((currentState) => {
      const remaining = currentState.blocks.filter((block) => block.id !== blockId);
      return {
        ...currentState,
        selectedBlockId:
          currentState.selectedBlockId === blockId
            ? remaining[0]?.id || ""
            : currentState.selectedBlockId,
        blocks: remaining,
      };
    });

  const moveBlock = (blockId: string, direction: -1 | 1) =>
    updateBuilder((currentState) => {
      const currentIndex = currentState.blocks.findIndex(
        (block) => block.id === blockId
      );
      const nextIndex = currentIndex + direction;
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= currentState.blocks.length) {
        return currentState;
      }
      const nextBlocks = [...currentState.blocks];
      const [moved] = nextBlocks.splice(currentIndex, 1);
      nextBlocks.splice(nextIndex, 0, moved);
      return { ...currentState, blocks: nextBlocks };
    });

  // Template Settings tab: background color drives an automatic readable text
  // color ("Text color will be automatically selected based on background color.").
  const setTemplateBackgroundColor = (hex: string) =>
    updateBuilder((currentState) => ({
      ...currentState,
      theme: {
        ...currentState.theme,
        backgroundColor: hex,
        textColor: getReadableTextColor(hex),
      },
    }));

  const previewToggleButtonClassName = (active: boolean) =>
    `rounded-md p-3 transition ${
      active ? "bg-gray-100 text-gray-950" : "text-gray-400 hover:text-gray-600"
    }`;

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={onBackToTemplates}
          disabled={disabled}
          className="inline-flex items-center gap-2 text-[17px] font-extrabold text-gray-950 transition hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ArrowBackIcon fontSize="small" />
          Back to Templates
        </button>
        <h2 className="mt-7 text-[22px] font-extrabold text-gray-950">
          Email Template: {templateTitle}
        </h2>
      </div>

      <div className="rounded-md border border-gray-200 bg-white px-6 py-4 text-center">
        <p className="text-[18px] font-extrabold text-gray-950">Preview</p>
        <div className="mt-3 flex justify-center gap-3">
          <button
            type="button"
            title="Desktop preview"
            aria-label="Desktop preview"
            className={previewToggleButtonClassName(previewMode === "desktop")}
            onClick={() => setPreviewMode("desktop")}
          >
            <DesktopWindowsOutlinedIcon />
          </button>
          <button
            type="button"
            title="Mobile preview"
            aria-label="Mobile preview"
            className={previewToggleButtonClassName(previewMode === "mobile")}
            onClick={() => setPreviewMode("mobile")}
          >
            <PhoneIphoneOutlinedIcon />
          </button>
        </div>
      </div>

      <p className="text-[17px] font-extrabold text-gray-950">
        Build your chamber newsletter using our pre-made templates.
      </p>

      <div className="grid grid-cols-1 gap-0 overflow-hidden rounded-md border border-gray-200 bg-white xl:grid-cols-[340px_minmax(440px,1fr)_360px]">
        {/* LEFT — Blocks palette */}
        <aside
          ref={paletteRef}
          className="border-b border-gray-200 bg-white p-6 xl:border-b-0 xl:border-r"
        >
          <h3 className="text-[17px] font-extrabold text-gray-950">Blocks</h3>
          <p className="mt-2 text-[14px] leading-5 text-gray-700">
            Select a pre-made block and drag it into your newsletter template.
            Click on the block to easily duplicate it or move it. Then use
            Content builder on the right to add content.
          </p>
          <div className="mt-6 space-y-5">
            {emailBuilderPaletteItems.map((item) => (
              <button
                key={item.key}
                type="button"
                draggable={!disabled}
                disabled={disabled}
                onDragStart={(event) => {
                  event.dataTransfer.setData("text/plain", item.key);
                }}
                onClick={() => addBlock(item.key)}
                title={`Add the ${item.label} block to your email`}
                className="block w-full rounded-md text-left transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <p className="mb-1.5 text-[13px] font-extrabold leading-4 text-gray-950">
                  {item.label}
                </p>
                <img
                  src={item.previewImage}
                  alt={`${item.label} block preview`}
                  className="w-full rounded-md border border-gray-200 bg-white object-contain"
                />
              </button>
            ))}
          </div>
        </aside>

        {/* CENTER — email canvas */}
        <main
          className="bg-gray-50 p-8"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            if (disabled) return;
            const type = event.dataTransfer.getData(
              "text/plain"
            ) as EmailBuilderBlockType;
            if (emailBuilderPaletteItems.some((item) => item.key === type)) {
              addBlock(type);
            }
          }}
        >
          <div
            className={`mx-auto min-h-[760px] border border-gray-200 shadow-sm transition-all ${
              previewMode === "mobile" ? "max-w-[390px]" : "max-w-[720px]"
            }`}
            style={{
              backgroundColor: builderState.theme.backgroundColor,
              fontFamily: `'${builderState.fontFamily || "Roboto"}', Arial, Helvetica, sans-serif`,
            }}
          >
            <div className="px-7 pt-7 text-center">
              {builderState.logoUrl ? (
                <img
                  src={builderState.logoUrl}
                  alt="Chamber logo"
                  className="mx-auto max-h-24 max-w-[220px] object-contain"
                />
              ) : (
                <p className="font-extrabold text-gray-500">Chamber Logo</p>
              )}
            </div>
            {builderState.blocks.length === 0 ? (
              <div className="mx-7 my-8 rounded-md bg-gray-50 px-8 py-14 text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-950 text-white">
                  <ForwardToInboxOutlinedIcon sx={{ fontSize: 32 }} />
                </span>
                <p className="mt-5 text-[26px] font-extrabold text-gray-950">
                  Your Message, Your Way.
                </p>
                <p className="mx-auto mt-2 max-w-sm text-[15px] leading-6 text-gray-600">
                  Use our drag-and-drop builder to create a custom email that
                  fits your audience and your goals.
                </p>
                <button
                  type="button"
                  className="mt-6 inline-flex min-h-[44px] items-center rounded-md bg-[#3B82F6] px-7 text-[14px] font-extrabold tracking-wide text-white transition hover:bg-blue-600"
                  onClick={() =>
                    paletteRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    })
                  }
                >
                  Start Building
                </button>
              </div>
            ) : (
              <div className="mt-4">
                {builderState.blocks.map((block) => (
                  <CanvasBlockShell
                    key={block.id}
                    block={block}
                    selected={block.id === builderState.selectedBlockId}
                    disabled={disabled}
                    onSelect={() => selectBlock(block.id)}
                    onMove={(direction) => moveBlock(block.id, direction)}
                    onDuplicate={() => duplicateBlock(block.id)}
                    onDelete={() => requestDelete(() => deleteBlock(block.id))}
                  />
                ))}
              </div>
            )}
            <CanvasFooter
              builderState={builderState}
              footerProfile={footerProfile}
            />
          </div>
        </main>

        {/* RIGHT — settings panel */}
        <aside className="border-t border-gray-200 bg-white xl:border-l xl:border-t-0">
          <div className="border-b border-gray-200 p-6">
            <h3 className="text-[17px] font-extrabold text-gray-950">
              {settingsTab === "content"
                ? "Update Block Contents"
                : "Update Template Settings"}
            </h3>
            <p className="mt-2 text-[14px] leading-5 text-gray-700">
              {settingsTab === "content"
                ? "Use the content builder to add text, images, and updates to each section of your newsletter. Simply click on any block to edit the content, replace images, or customize the layout."
                : "Select the background colour for your template and emails. The text colour will automatically be selected to ensure your content remains clear and easy to read."}
            </p>
          </div>
          <div className="grid grid-cols-2 border-b border-gray-200 text-center">
            <button
              type="button"
              onClick={() => setSettingsTab("content")}
              className={`py-4 font-extrabold transition ${
                settingsTab === "content"
                  ? "border-b-2 border-cpaGold text-cpaGold"
                  : "text-gray-700 hover:text-gray-950"
              }`}
            >
              Content Builder
            </button>
            <button
              type="button"
              onClick={() => setSettingsTab("template")}
              className={`py-4 font-extrabold transition ${
                settingsTab === "template"
                  ? "border-b-2 border-cpaGold text-cpaGold"
                  : "text-gray-700 hover:text-gray-950"
              }`}
            >
              Template Settings
            </button>
          </div>
          <div className="space-y-5 bg-gray-50 p-6">
            {settingsTab === "template" ? (
              <>
                <ColorSwatchRow
                  label="BACKGROUND COLOR"
                  color={builderState.theme.backgroundColor}
                  quickPalette={quickPalette}
                  disabled={disabled}
                  onChange={setTemplateBackgroundColor}
                />
                <p className="text-[13px] leading-5 text-gray-600">
                  Text color will be automatically selected based on background
                  color.
                </p>
                <SettingsField label="CHOOSE FONT">
                  <FontSelectDropdown
                    value={builderState.fontFamily || "Roboto"}
                    disabled={disabled}
                    onChange={(font) =>
                      updateBuilder((currentState) => ({
                        ...currentState,
                        fontFamily: font,
                      }))
                    }
                  />
                </SettingsField>
              </>
            ) : selectedBlock ? (
              <BlockSettingsPanel
                block={selectedBlock}
                patchBlockSettings={patchBlockSettings}
                disabled={disabled}
                quickPalette={quickPalette}
                uploadImage={uploadImage}
                requestDelete={requestDelete}
                chamberId={chamberId}
                chamberWebsiteUrl={chamberWebsiteUrl}
              />
            ) : (
              <p className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-600">
                Click any block on the email preview to edit its content here.
                You can also drag a new block in from the left panel.
              </p>
            )}
          </div>
        </aside>
      </div>

      <DeleteConfirmModal
        open={!!pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          pendingDelete?.();
          setPendingDelete(null);
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Template gallery (EMAIL-007 — verbatim per the two gallery screenshots)
// ---------------------------------------------------------------------------

const gallerySkeletonBar = (width: string, key: number) => (
  <span key={key} className={`block h-2 rounded bg-gray-200 ${width}`} />
);

const galleryBannerClassName =
  "rounded bg-[#3B82F6] px-3 py-2 text-center text-[11px] font-extrabold uppercase tracking-wide text-white";
const galleryBlueButtonClassName =
  "inline-block rounded bg-[#3B82F6] px-3 py-1.5 text-[11px] font-extrabold text-white";

// Mock preview interiors per gallery card (SAMPLE content, transcribed).
const TemplatePreviewMock = ({ templateKey }: { templateKey: EmailTemplateKey }) => {
  switch (templateKey) {
    case "newsletter":
      return (
        <div className="space-y-2.5">
          <div className="rounded bg-[#3B82F6] px-3 py-3 text-center text-white">
            <p className="text-[9px] font-extrabold uppercase tracking-wide">
              JUNE 2025 NEWSLETTER
            </p>
            <p className="mt-1 text-[15px] font-extrabold">Chamber Insider</p>
            <span className="mt-2 inline-block rounded bg-gray-200 px-2.5 py-1 text-[10px] font-extrabold text-gray-700">
              Read Story
            </span>
          </div>
          {[0, 1, 2].map((index) =>
            gallerySkeletonBar(index === 1 ? "w-3/4" : "w-full", index)
          )}
        </div>
      );
    case "eblast":
      return (
        <div className="space-y-2.5 text-center">
          <p className={galleryBannerClassName}>IMPORTANT UPDATE</p>
          <p className="text-[14px] font-extrabold leading-5 text-gray-950">
            New Provincial Grant Now Available!
          </p>
          <span className={galleryBlueButtonClassName}>Apply Now</span>
          {[0, 1].map((index) => gallerySkeletonBar(index === 1 ? "w-2/3" : "w-full", index))}
        </div>
      );
    case "advocacyUpdate":
      return (
        <div className="space-y-2.5 text-center">
          <p className={galleryBannerClassName}>ADVOCACY UPDATE</p>
          <p className="text-[14px] font-extrabold leading-5 text-gray-950">
            Support Smart Growth In Our Community
          </p>
          <span className={galleryBlueButtonClassName}>Take the Survey</span>
          {gallerySkeletonBar("w-3/4 mx-auto", 0)}
        </div>
      );
    case "eventAnnouncement":
      return (
        <div className="space-y-2.5">
          <p className={galleryBannerClassName}>
            BUSINESS NETWORKING
            <br />
            LUNCHEON
          </p>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-800">
            <CalendarMonthOutlinedIcon sx={{ fontSize: 13 }} className="text-[#3B82F6]" />
            Tuesday, June 24, 2025
          </p>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-800">
            <AccessTimeOutlinedIcon sx={{ fontSize: 13 }} className="text-[#3B82F6]" />
            11:30 AM - 1:30 PM
          </p>
          <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-800">
            <PlaceOutlinedIcon sx={{ fontSize: 13 }} className="text-red-500" />
            Harbour View Event Centre
          </p>
          <span className={`${galleryBlueButtonClassName} block w-full text-center`}>
            Register Now
          </span>
        </div>
      );
    case "mediaRelease":
      return (
        <div className="space-y-2.5 text-center">
          <p className={galleryBannerClassName}>MEDIA RELEASE</p>
          <p className="text-[14px] font-extrabold leading-5 text-gray-950">
            Sand Bay Chamber Announces New Board of Directors
          </p>
          <span className={galleryBlueButtonClassName}>Read Full Release</span>
          {[0, 1].map((index) => gallerySkeletonBar(index === 1 ? "w-2/3" : "w-full", index))}
        </div>
      );
    case "memberWelcome":
      return (
        <div className="space-y-2.5 text-center">
          <p className={galleryBannerClassName}>WELCOME TO THE CHAMBER!</p>
          <p className="text-[12px] leading-5 text-gray-800">
            {"Hi {First Name}, We're excited to have you part of our business community."}
          </p>
          {[0, 1].map((index) => gallerySkeletonBar(index === 1 ? "w-2/3 mx-auto" : "w-full", index))}
        </div>
      );
    case "fromTheDesk":
      return (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between gap-2 rounded bg-[#3B82F6] px-3 py-2.5 text-white">
            <p className="text-[12px] font-extrabold leading-4">
              From the Desk
              <br />
              Of Our CEO
            </p>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/25">
              <PersonSearchOutlinedIcon sx={{ fontSize: 18 }} />
            </span>
          </div>
          <p className="text-[11px] leading-4 text-gray-600">
            Dear Members,
            <br />
            I'm proud to share some exciting updates about our chamber...
          </p>
          <p className="text-[12px] italic text-gray-800">Karen Hattie</p>
        </div>
      );
    case "customEmail":
      return (
        <div className="space-y-2.5 text-center">
          <div className="rounded bg-gray-100 px-3 py-4">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-gray-400">
              <AddIcon />
            </span>
          </div>
          <p className="text-[14px] font-extrabold text-gray-950">
            Your Message, Your Way.
          </p>
          <p className="text-[11px] leading-4 text-gray-600">
            Use our drag-and-drop builder to create a custom email that fits
            your brand.
          </p>
          <span className="inline-block rounded bg-gray-950 px-3 py-1.5 text-[11px] font-extrabold text-white">
            Start Building
          </span>
        </div>
      );
    default:
      return null;
  }
};

const GalleryCallout = ({
  label,
  copy,
}: {
  label: "Best For:" | "Design Tip:";
  copy: string;
}) => (
  <div className="flex items-start gap-2.5 rounded-md border border-gray-200 bg-white p-3">
    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cpaGold text-white">
      <CheckIcon sx={{ fontSize: 13 }} />
    </span>
    <p className="text-[13px] leading-5 text-gray-950">
      <span className="font-extrabold">{label}</span> {copy}
    </p>
  </div>
);

const TemplateGallery = ({
  disabled,
  onSelect,
}: {
  disabled: boolean;
  onSelect: (preset: EmailTemplatePreset) => void;
}) => (
  <section className={sectionClassName}>
    <div className="grid grid-cols-1 gap-x-6 gap-y-10 md:grid-cols-2 xl:grid-cols-4">
      {emailTemplatePresets.map((preset, index) => (
        <div key={preset.key} className="flex flex-col">
          <h3 className="text-[17px] font-extrabold uppercase text-gray-950">
            {index + 1}. {preset.title}
          </h3>
          <p className="mt-1.5 min-h-[44px] text-[14px] leading-5 text-black">
            {preset.description}
          </p>
          <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <p className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-[12px] font-extrabold text-gray-950">
              Content Builder - Template Preview
            </p>
            <div className="p-3">
              <div className="min-h-[190px] rounded border border-dashed border-gray-300 p-3">
                <TemplatePreviewMock templateKey={preset.key} />
              </div>
            </div>
          </div>
          <button
            type="button"
            className={`${primaryButtonClassName} mt-4 w-full`}
            onClick={() => onSelect(preset)}
            disabled={disabled}
          >
            Select
          </button>
          <div className="mt-4 space-y-3">
            <GalleryCallout label="Best For:" copy={preset.bestFor} />
            <GalleryCallout label="Design Tip:" copy={preset.designTip} />
          </div>
        </div>
      ))}
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Step 3 root — gallery phase, then builder phase
// ---------------------------------------------------------------------------

export type StepThreeContentProps = {
  email: EmailFormState;
  isBusy: boolean;
  isSentRecord: boolean;
  updateBuilderState: UpdateEmailBuilderState;
  uploadImage: (file: File) => Promise<string>;
  /** Optional: persists the selected template key onto the form state. */
  updateEmailField?: UpdateEmailField;
};

const StepThreeContent = ({
  email,
  isBusy,
  isSentRecord,
  updateBuilderState,
  uploadImage,
  updateEmailField,
}: StepThreeContentProps) => {
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const chamberWebsiteUrl = chamber?.chamberMemberProWebsiteUrl;
  const disabled = isBusy || isSentRecord;

  // Drafts/updates that already picked a template (or carry legacy canvas
  // content) skip straight to the builder; fresh campaigns start at the
  // template gallery.
  const [activeTemplateKey, setActiveTemplateKey] = useState<EmailTemplateKey | null>(
    () =>
      email.templateId ||
      (email.builderState.blocks.length > 0 ? "customEmail" : null)
  );
  const [showBackConfirm, setShowBackConfirm] = useState(false);

  const activePreset = emailTemplatePresets.find(
    (preset) => preset.key === activeTemplateKey
  );

  const selectTemplate = (preset: EmailTemplatePreset) => {
    const blocks = preset.orderedBlocks;
    updateBuilderState((currentState) => ({
      ...currentState,
      blocks,
      selectedBlockId: blocks[0]?.id || "",
    }));
    updateEmailField?.("templateId", preset.key);
    setActiveTemplateKey(preset.key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const leaveBuilder = () => {
    if (email.builderState.blocks.length > 0) {
      updateBuilderState((currentState) => ({
        ...currentState,
        blocks: [],
        selectedBlockId: "",
      }));
    }
    updateEmailField?.("templateId", "");
    setActiveTemplateKey(null);
    setShowBackConfirm(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // EMAIL-CREATE-015: Back to Templates returns to the gallery; content on
  // the canvas requires an explicit styled confirmation before it is
  // discarded (native window.confirm is inconsistent with the module's
  // dialog pattern).
  const backToTemplates = () => {
    if (email.builderState.blocks.length > 0) {
      setShowBackConfirm(true);
      return;
    }
    leaveBuilder();
  };

  if (!activePreset) {
    return (
      <div className="space-y-8">
        <div>
          <h2 className={stepTitleClassName}>Content Builder</h2>
          <p className={stepDescriptionClassName}>
            Select a pre-designed template optimized to ensure highest open and
            click rates for your chamber communications. All templates are fully
            customizable and mobile-responsive.
          </p>
        </div>
        <TemplateGallery disabled={disabled} onSelect={selectTemplate} />
      </div>
    );
  }

  return (
    <>
      <CustomEmailBuilder
        builderState={email.builderState}
        templateTitle={activePreset.builderTitle}
        disabled={disabled}
        chamberId={chamberId}
        chamberWebsiteUrl={chamberWebsiteUrl}
        footerProfile={chamber}
        uploadImage={uploadImage}
        onChange={updateBuilderState}
        onBackToTemplates={backToTemplates}
      />
      {showBackConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="relative w-full max-w-md rounded-xl bg-white p-8 text-center shadow-2xl">
            <button
              type="button"
              onClick={() => setShowBackConfirm(false)}
              aria-label="Close"
              className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white shadow transition hover:bg-red-600"
            >
              <CloseIcon fontSize="small" />
            </button>
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-500 text-white">
              <WarningAmberOutlinedIcon sx={{ fontSize: 34 }} />
            </span>
            <h3 className="mt-4 text-[22px] font-extrabold text-gray-950">
              Back to Templates?
            </h3>
            <p className="mt-2 text-[15px] leading-6 text-gray-600">
              Going back to templates will remove the content currently on your
              email canvas.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                type="button"
                onClick={leaveBuilder}
                className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-cpaGold px-6 text-[14px] font-extrabold text-white transition hover:bg-cpaGoldDark"
              >
                YES
              </button>
              <button
                type="button"
                onClick={() => setShowBackConfirm(false)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-gray-900 px-6 text-[14px] font-extrabold text-white transition hover:bg-black"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default StepThreeContent;
